import { cloneDeep, isArray, truncate } from 'lodash';
import { Utils as QbUtils } from 'react-awesome-query-builder';

// Components
import { ScenarioBlacklistSelectorNew } from 'app/modules/detectionModels/components/scenarioWidgets/ScenarioBlacklistSelectorNew';
import { ScenarioPerBlacklistEditor } from 'app/modules/detectionModels/components/scenarioWidgets/ScenarioPerBlacklistEditorNew';
import ScenarioSequenceCodeEditor from 'app/modules/detectionModels/components/scenarioWidgets/ScenarioSequenceCodeEditor';
import { EntityLinkCountMockGraph } from 'app/modules/detectionModels/components/scenarioWidgets/EntityLinkCountMockGraph';

// Selectors
import { ComponentRulesSelector } from 'app/modules/detectionModels/components/scenarioWidgets/ComponentRulesSelector';

// Models
import {
  DetectionModel,
  DetectionModelMetadata,
  DetectionModelTemplate,
  DetectionModelValidation,
  EditingAdvancedDetectionModel,
  EditingDetectionModel,
  EditingSimpleDetectionModel,
  ModelValidationPayload,
  QueryTreeFiltersModel,
  RuleGraphMatches,
  ScenarioConfig,
  ScenarioConfigs,
  ScenarioPayload,
  SimpleModelStep,
  ValidateModelPayload,
} from 'app/modules/detectionModels/models';
import {
  RtrDecision,
  RuleScenarioFilters,
  ScenarioConstants,
} from 'app/modules/rules/models';
import { OrgScenarioConfig } from 'app/modules/orgSettings/models';
import {
  CompositeOperator,
  CompositeQueryTreeNode,
  CompositeTypes,
  ConstantValue,
  DmAggregateFunctions,
  DmDurationUnits,
  DmEventByObjectFact,
  DmObjectToFlag,
  DmOperator,
  FieldValue,
  IRSchema,
  MultiplePerspectivesAggregation,
  QueryTreeNode,
  ValueTypes,
  VariableValue,
} from 'app/modules/rulesAdvanced/models';
import {
  ScenarioBuilderWidgets,
  WidgetProps,
} from 'app/modules/detectionModels/components/scenarioWidgets/models';
import {
  ConditionOperand,
  FieldValueOperand,
  OperandSymbol,
  OperandTypes,
  SymbolFieldValue,
  SymbolTable,
} from 'app/modules/rulesAdvanced/lib/SymbolTable';
import { U21SelectOptionProps } from 'app/shared/u21-ui/components';
import { BlacklistType } from 'app/modules/lists/models';
import {
  CustomDataSettingsConfigResponse,
  DataSettingFieldType,
  DataSettingsKeyType,
  NativeDataSettingsConfigResponse,
  NativeEntityDataSettingsConfigResponse,
  NativeInstrumentDataSettingsConfigResponse,
  Unit21DataClassifier,
} from 'app/modules/dataSettings/responses';
import { DmbQueryBuilderOption } from 'app/modules/rulesAdvanced/utils/SymbolTableBuilder';
import {
  AndOrCondition,
  Condition,
  ConditionOperandValue,
} from 'app/modules/detectionModels/components/DropdownBuilder/models';

// Constants
import {
  ACH_RISK_SCORE_ENABLED_SCENARIO_RULES,
  CHECK_FRAUD_RULES_ALL,
  ConditionalOperators,
  CONSORTIUM_RULES,
  DORMANT_ACTIVITY_NAME,
  DYNAMIC_RULE_NAME,
  FLAGGED_OBJECT_PARAMETER,
  IN_OPERATORS,
  LEGACY_SEQUENCE_RULES,
  MAX_DROPDOWN_OPTION_DESCRIPTION_LENGTH,
  OPERAND_TYPE_OPERATOR_OPTIONS_MAP,
  SCENARIO_ACH_CONSORTIUM_NAME,
  SCENARIO_ACH_DUPLICATE_NAME,
  SCENARIO_ACH_MISMATCHED_NAME,
  SCENARIO_ACH_RISK_SCORE_NAME,
  SCENARIO_BUILDER_CHECK_FRAUD_RULES,
  SCENARIO_CONSORTIUM_NEW_ACCOUNT_NAME,
  SCENARIO_DUPLICATE_SERIAL_NUMBER_NAME,
  SCENARIO_MISSING_SERIAL_NUMBER_NAME,
  SCENARIO_MULTIPLE_FAILED_VALIDATION_ATTEMPTS_NAME,
  SCENARIO_SEQUENCE_CUMULATIVE_AGGREGATE_NAME,
  SCENARIO_SEQUENCE_CUMULATIVE_AGGREGATE_USPS_NAME,
  SCENARIO_SEQUENCE_DAILY_AGGREGATE_NAME,
  SCENARIO_SEQUENCE_ROLLING_WINDOW_NAME,
  SCENARIO_SEQUENCE_ROLLING_WINDOW_ONE_STEP_NAME,
  SCENARIO_SEQUENCE_ROLLING_WINDOW_ROUND_TRIP_NAME,
  SCENARIO_SEQUENCE_ROLLING_WINDOW_WITH_COUNT_NAME,
  SIMPLE_MODEL_STEPS,
  WATCHLIST_RULE_NAME,
} from 'app/modules/detectionModels/constants';
import {
  BE_DATE_FORMAT,
  INPUT_FIELD_DATETIME_VALUE_FORMAT,
} from 'app/shared/constants';
import { DECISION_MAP, TIME_STRING_FORMAT } from 'app/modules/rules/constants';
import { MONTHLY_TIME_WINDOW_VALUES } from 'app/modules/rules/config/timeWindow';
import { NATIVE_DATA_SETTINGS_NATIVE_KEY_MISMATCH_MAP } from 'app/modules/dataSettings/constants';
import { TXN_EVENT_COMMON_DROPDOWN_SYMBOLS } from 'app/modules/detectionModels/txnEventCommonDropdownSymbols';
import { TXN_EVENT_SENDER_RECEIVER_ENTITY_DROPDOWN_SYMBOLS } from 'app/modules/detectionModels/txnEventSenderReceiverEntityDropdownSymbols';
import { ACTION_EVENT_ADVANCED_SYMBOLS } from 'app/modules/detectionModels/actionEventAdvancedSymbols';
import { ACTION_EVENT_DROPDOWN_SYMBOLS } from 'app/modules/detectionModels/actionEventDropdownSymbols';
import { TXN_EVENT_COMMON_ADVANCED_SYMBOLS } from 'app/modules/detectionModels/txnEventCommonAdvancedSymbols';
import { TXN_EVENT_SENDER_RECEIVER_ADVANCED_SYMBOLS } from 'app/modules/detectionModels/txnEventSenderReceiverEntityAdvancedSymbols';

// Helpers
import { consoleError } from 'app/shared/utils/console';
import { getBEDateFormat, getLocalFormat } from 'app/shared/utils/timeHelpers';
import {
  DURATION_UNIT_MAP,
  getWindowSizeFromIR,
  MONTH_UNIT_MAP,
  TIME_CONVERSION,
} from 'app/modules/rulesAdvanced/lib/IRHelpers';
import { format, isLastDayOfMonth, subMinutes } from 'date-fns';
import { keyPathToLabel } from 'app/modules/dataSettings/utils';
import { toIrSchemaOrNull } from 'app/modules/rulesAdvanced/helper';
import { toSentenceCase } from 'app/shared/utils/string';
import { ShortEntityResponse } from 'app/modules/entities/responses';
import { U21NetworkGraphElements } from 'app/shared/components/Graphs/U21NetworkGraph';
import type { U21Palette } from 'vendor/material-minimal/palette';
import { SupportRequestLink } from 'app/modules/detectionModels/components/SupportRequestLink';
import {
  RuleExecutionFrequencyBehavior,
  ShortRuleResponse,
} from 'app/modules/rules/types/responses';
import ordinalize from 'app/shared/utils/ordinalize';
import {
  getFieldValueOperandFromSymbolTable,
  isFieldValue,
  isVariableValue,
} from 'app/modules/detectionModels/dropdownBuilderHelpers';
import { DropdownBuilderFormUiTypes } from 'app/modules/detectionModels/components/DropdownBuilder/NextStepSelector';
import {
  buildTrmFieldsForAdvancedBuilder,
  buildTrmFieldsForDropdownBuilder,
} from 'app/modules/detectionModels/trmFields';
import {
  DmbQueryBuilderOptionCode,
  DmbQueryBuilderOptionCodeFriendlyName,
  Prefix,
} from 'app/modules/detectionModels/dmbTypes';

// NOTE: used where ever model needs to be lowercased
const DM_OBJECT_ENUM_TO_CODE_MAP: {
  [key in Extract<
    DmbQueryBuilderOption,
    'ACTION_EVENT' | 'TXN_EVENT'
  >]: DmbQueryBuilderOptionCode;
} = {
  TXN_EVENT: 'txn_event',
  ACTION_EVENT: 'action_event',
};

export const U21_LATEST_RISK_SCORE_KEY = 'u21_latest_risk_score';
export const U21_RISK_LEVEL_KEY = 'u21_latest_risk_level';
const U21_RISK_LEVELS = ['Low', 'Medium', 'High'];

export const getValidationPayload = (
  metadata: DetectionModelMetadata | null,
  validationPayload: ModelValidationPayload | null,
  dataMigrationLag: number,
): ModelValidationPayload => {
  const isNewRule =
    metadata === null ||
    (metadata?.title === '' && metadata?.description === '');
  const isActiveRule =
    validationPayload !== null &&
    validationPayload.start_time !== '' &&
    validationPayload.end_time === '';

  const EXTRA_BUFFER_FOR_SF_DATA_MIGRATION_LAG = 60;

  let subAmount = 0;
  if (validationPayload === null) {
    subAmount = 43800;
  } else if (isNewRule) {
    subAmount = dataMigrationLag * 2;
  }

  return {
    execution_frequency_behavior:
      validationPayload?.execution_frequency_behavior || null,
    execution_frequency:
      validationPayload === null ? '' : validationPayload.execution_frequency,
    start_time: format(
      subMinutes(
        validationPayload === null || validationPayload.start_time === ''
          ? new Date()
          : new Date(validationPayload.start_time),
        subAmount,
      ),
      TIME_STRING_FORMAT,
    ),
    end_time: format(
      subMinutes(
        validationPayload === null || validationPayload.end_time === ''
          ? new Date()
          : new Date(validationPayload.end_time),
        isNewRule || isActiveRule
          ? dataMigrationLag + EXTRA_BUFFER_FOR_SF_DATA_MIGRATION_LAG
          : 0,
      ),
      TIME_STRING_FORMAT,
    ),
  };
};

// =========== Shared DM Helpers =============
export const editingValidatedModel = (
  detectionModelValidation: EditingDetectionModel,
): boolean => {
  return Boolean(detectionModelValidation.ruleId);
};

export const isMonthlyExecution = (
  executionFrequency: string | undefined,
): boolean => {
  return (
    executionFrequency !== undefined &&
    executionFrequency !== '' &&
    MONTHLY_TIME_WINDOW_VALUES.includes(executionFrequency)
  );
};

export const shouldShowMonthlyExecutionBehavior = (
  startTime: string | undefined,
  executionFrequency: string | undefined,
): boolean => {
  if (startTime === undefined || !isMonthlyExecution(executionFrequency)) {
    return false;
  }

  const date = new Date(startTime);

  // Check if it's the last day of the month
  if (!isLastDayOfMonth(date)) {
    return false;
  }

  // Get the day of month (1-31)
  const dayOfMonth = date.getDate();

  // Only show the dropdown if it's the last day of a month with less than 31 days
  // Feb has 28/29 days, Apr/Jun/Sep/Nov have 30 days
  // Months with 31 days (Jan/Mar/May/Jul/Aug/Oct/Dec) should NOT show the dropdown
  return dayOfMonth < 31;
};

export const getMonthlyExecutionBehaviorOptions = (
  startDate: Date | undefined,
): Array<{
  text: string;
  value: RuleExecutionFrequencyBehavior | null;
}> => {
  const startDay = startDate?.getDate();
  const scheduledDayText = startDay
    ? `Execute monthly on the ${startDay}${ordinalize(startDay)} day`
    : 'Execute monthly on the scheduled day';

  return [
    {
      text: 'Execute monthly on the last day',
      value: 'END_OF_MONTH',
    },
    {
      text: scheduledDayText,
      value: null,
    },
  ];
};

export const getModelMetadataFromValidation = ({
  title,
  id,
  description,
  tags,
  customer_support_involved: customerSupportInvolved,
  queue,
  runs_on_org_id: runsOnOrgID,
  visible_to_child_org: visibleToChildOrg,
  decision,
}: {
  title: string;
  description: string;
  id: number;
  tags: number[];
  queue: number | null;
  runs_on_org_id: number | null;
  customer_support_involved: boolean | null;
  visible_to_child_org: boolean | null;
  decision?: RtrDecision;
}): DetectionModelMetadata => {
  return {
    customer_support_involved: Boolean(customerSupportInvolved),
    title,
    description,
    tags: tags || [],
    queue,
    parentRuleId: id,
    runs_on_org_id: runsOnOrgID,
    visible_to_child_org: Boolean(visibleToChildOrg),
    decision,
  };
};

export const getValidationPayloadFromValidation = (
  detectionModelValidation: Pick<
    DetectionModelValidation,
    | 'exec_range_end'
    | 'exec_range_start'
    | 'execution_frequency'
    | 'content'
    | 'execution_frequency_behavior'
  >,
  dateFormat = INPUT_FIELD_DATETIME_VALUE_FORMAT,
): ModelValidationPayload => {
  const startTime = getLocalFormat(
    detectionModelValidation.exec_range_start,
    dateFormat,
    BE_DATE_FORMAT,
  );
  const endTime = getLocalFormat(
    detectionModelValidation.exec_range_end,
    dateFormat,
    BE_DATE_FORMAT,
  );

  const executionFrequency = detectionModelValidation.content.scenario
    .parameters.$window_stride
    ? detectionModelValidation.content.scenario.parameters.$window_stride
    : '';
  return {
    start_time: startTime,
    end_time: endTime,
    execution_frequency:
      detectionModelValidation.execution_frequency || executionFrequency,
    execution_frequency_behavior:
      detectionModelValidation.execution_frequency_behavior || null,
  };
};

// =========== Simple DM Helpers =============
export const getEditingSimpleDetectionModelFromValidation = (
  detectionModelValidation: Pick<
    DetectionModelValidation,
    | 'content'
    | 'id'
    | 'title'
    | 'description'
    | 'tags'
    | 'exec_range_end'
    | 'exec_range_start'
    | 'execution_frequency'
    | 'execution_frequency_behavior'
    | 'customer_support_involved'
    | 'queue'
    | 'runs_on_org_id'
    | 'visible_to_child_org'
    | 'decision'
  >,
): EditingSimpleDetectionModel => {
  const { scenario, specification } = detectionModelValidation.content;
  const embeddedFiltersAsQueryTree = scenarioFiltersToEmbeddedFilters(
    scenario.filters,
  );

  return {
    metadata: getModelMetadataFromValidation(detectionModelValidation),
    validationPayload: getValidationPayloadFromValidation(
      detectionModelValidation,
    ),
    objectToFlag: scenario.parameters[FLAGGED_OBJECT_PARAMETER] || null,
    scenario: {
      name: scenario.name,
      embeddedFilters: embeddedFiltersAsQueryTree,
      parameters: scenario.parameters,
    },
    ruleId: detectionModelValidation.id,
    specification,
  };
};

export const getDMValidationPayload = (
  editingSimpleDetectionModel: EditingSimpleDetectionModel,
  dateFormat = INPUT_FIELD_DATETIME_VALUE_FORMAT,
): ValidateModelPayload => {
  const startTime = getBEDateFormat(
    editingSimpleDetectionModel.validationPayload?.start_time || '',
    dateFormat,
  );
  const endTime = getBEDateFormat(
    editingSimpleDetectionModel.validationPayload?.end_time || '',
    dateFormat,
  );
  const embeddedFiltersQueryTreeAsJson = embeddedFiltersToJson(
    editingSimpleDetectionModel.scenario?.embeddedFilters || {},
  );

  return {
    // Scenario content
    contents: {
      scenario: {
        scenario_name: editingSimpleDetectionModel.scenario?.name || '',
        parameters: {
          ...editingSimpleDetectionModel.scenario?.parameters,

          $time_window:
            editingSimpleDetectionModel.scenario?.parameters.$time_window || '',
          $window_stride:
            editingSimpleDetectionModel.validationPayload
              ?.execution_frequency || '',
        },
        filters: embeddedFiltersQueryTreeAsJson,
      },
      specification: editingSimpleDetectionModel.specification,
      execution_frequency:
        editingSimpleDetectionModel.validationPayload?.execution_frequency ||
        '',

      // TODO remove this, it was intended for old filters
      query_tree: {},
      inclusion_tags: [],
      inclusion_tag_names: [],
      exclusion_tags: [],
      exclusion_tag_names: [],
    },

    // Validation time range
    exec_range_start: startTime,
    exec_range_end: endTime,

    // Add metadata
    tag_ids: editingSimpleDetectionModel.metadata?.tags || [],
    metadata: {
      title: editingSimpleDetectionModel.metadata?.title || '',
      description: editingSimpleDetectionModel.metadata?.description || '',
      customer_support_involved:
        editingSimpleDetectionModel.metadata?.customer_support_involved ||
        false,
      parentRuleId:
        editingSimpleDetectionModel.metadata?.parentRuleId ||
        editingSimpleDetectionModel.ruleId ||
        undefined,
      exec_start_date_time: startTime,
      exec_end_date_time: endTime,
      execution_frequency_behavior:
        editingSimpleDetectionModel.validationPayload
          ?.execution_frequency_behavior || null,
    },
    runs_on_org_id: editingSimpleDetectionModel.metadata?.runs_on_org_id,
    visible_to_child_org: Boolean(
      editingSimpleDetectionModel.metadata?.visible_to_child_org,
    ),
  };
};

export const getAvailableSteps = (
  editingModel: EditingSimpleDetectionModel,
  scenarioConfigs: ScenarioConfigs,
  orgScenarioConfig: OrgScenarioConfig,
  modelSteps = SIMPLE_MODEL_STEPS,
): Set<string> => {
  // Goes over the linked list until it can no longer continue, returns a set of visited steps
  const availableSteps = new Set<string>();

  const allSteps = Object.keys(modelSteps) as SimpleModelStep[];
  const nextSteps = Object.values(modelSteps).map(
    (stepConfig) => stepConfig.nextStep,
  );
  const initialStep = allSteps.filter((step) => !nextSteps.includes(step))[0];

  // Try to advance as many steps as possible
  if (initialStep) {
    let currentStep = initialStep;
    let canContinue = modelSteps[currentStep].canContinue(
      editingModel,
      scenarioConfigs,
    );
    let { nextStep } = modelSteps[currentStep];
    availableSteps.add(currentStep);
    while (canContinue && nextStep !== null) {
      currentStep = nextStep;
      canContinue = modelSteps[currentStep].canContinue(
        editingModel,
        scenarioConfigs,
      );
      nextStep = modelSteps[currentStep].nextStep;
      availableSteps.add(currentStep);
    }
  }

  return availableSteps;
};

export const getInitialStep = (
  editingModel: EditingSimpleDetectionModel,
  scenarioConfigs: ScenarioConfigs,
  orgScenarioConfig: OrgScenarioConfig,
  modelSteps = SIMPLE_MODEL_STEPS,
): SimpleModelStep => {
  const allSteps = Object.keys(modelSteps) as SimpleModelStep[];
  const nextSteps = Object.values(modelSteps).map(
    (stepConfig) => stepConfig.nextStep,
  );
  const initialStep = allSteps.filter((step) => !nextSteps.includes(step))[0];

  // Try to advance as many steps as possible
  if (initialStep) {
    let currentStep = initialStep;
    let canContinue = modelSteps[currentStep].canContinue(
      editingModel,
      scenarioConfigs,
    );
    let { nextStep } = modelSteps[currentStep];
    while (canContinue && nextStep !== null) {
      currentStep = nextStep;
      canContinue = modelSteps[currentStep].canContinue(
        editingModel,
        scenarioConfigs,
      );
      nextStep = modelSteps[currentStep].nextStep;

      // We can advance to the passed initialStep, return it
      if (currentStep === editingModel.initialStep) {
        return editingModel.initialStep;
      }
    }
    return currentStep;
  }

  // Default, return initial step
  return 'SCENARIO_BUILDER';
};

export const getPrevStep = (
  currentStep: SimpleModelStep,
  modelSteps = SIMPLE_MODEL_STEPS,
): SimpleModelStep | null => {
  const prevStep = Object.keys(modelSteps).filter(
    (step) => modelSteps[step].nextStep === currentStep,
  ) as SimpleModelStep[];
  if (prevStep.length > 0) {
    return prevStep[0];
  }
  return null;
};

export const getNextStep = (
  currentStep: SimpleModelStep,
  modelSteps = SIMPLE_MODEL_STEPS,
): SimpleModelStep | null => modelSteps[currentStep].nextStep;

export const isEditingAdvancedDetectionModel = (
  editingModel: EditingDetectionModel,
): editingModel is EditingAdvancedDetectionModel => {
  return 'irSchema' in editingModel;
};

export const isEditingSimpleDetectionModel = (
  editingModel: EditingDetectionModel,
): editingModel is EditingSimpleDetectionModel => {
  return 'scenario' in editingModel;
};

export const isDynamicModel = (scenario: string | null) =>
  scenario === 'Dynamic model';

export const isAdvancedModel = (
  detectionModel: DetectionModel | DetectionModelValidation | ShortRuleResponse,
): boolean => {
  return isDynamicModel(detectionModel.scenario);
};

export const getAlternateObjectType = (
  current: DmObjectToFlag,
): DmObjectToFlag =>
  current === Unit21DataClassifier.ENTITY
    ? Unit21DataClassifier.INSTRUMENT
    : Unit21DataClassifier.ENTITY;

export const getObjectTypeLabel = (objectType: DmObjectToFlag): string =>
  PRETTY_OBJECT_MAP[objectType];

export const isDuplicationEnabled = (
  detectionModel: DetectionModel | DetectionModelValidation,
  scenarioConfigs: Record<string, { enabled?: boolean }> | null,
): boolean =>
  isAdvancedModel(detectionModel) ||
  !!scenarioConfigs?.[detectionModel?.content?.scenario?.name || '']?.enabled;

export const isExternalRule = (
  detectionModel: Pick<DetectionModel, 'source' | 'content'>,
): boolean =>
  detectionModel.source === 'EXTERNAL' || detectionModel.content === null;

export const isDMBScenario = (scenarioName: string): boolean => {
  return scenarioName === DYNAMIC_RULE_NAME;
};
export const isRealTimeMonitoringModel = (
  detectionModel: DetectionModel | DetectionModelValidation,
): boolean =>
  detectionModel.content.scenario.parameters?.flag_single_object ?? false;

/**
 * Type guard to validate if a value is a valid DmObjectToFlag
 * Currently only supports TXN_EVENT and ACTION_EVENT
 */
export const isValidDmObjectToFlagTxnOrActionEvent = (
  value: string | null,
): value is DmObjectToFlag => {
  return value === 'ACTION_EVENT' || value === 'TXN_EVENT';
};
export const scenarioFiltersToEmbeddedFilters = (
  filters: RuleScenarioFilters = {},
): ScenarioPayload['embeddedFilters'] => {
  return Object.keys(filters).reduce(
    (accumulated: ScenarioPayload['embeddedFilters'], filterKey) => {
      const jsonFilter = filters[filterKey];
      return {
        ...accumulated,
        [filterKey]: {
          ...jsonFilter,
          query_tree: QbUtils.loadTree(jsonFilter.query_tree as any),
        },
      };
    },
    {},
  );
};

export const embeddedFiltersToJson = (
  embeddedFilters: ScenarioPayload['embeddedFilters'],
): RuleScenarioFilters => {
  return Object.keys(embeddedFilters).reduce((filters, filterKey) => {
    const qTreeFilter: QueryTreeFiltersModel = embeddedFilters[filterKey];
    return {
      ...filters,
      [filterKey]: {
        ...qTreeFilter,
        query_tree: qTreeFilter.query_tree.toJS(),
      },
    };
  }, {});
};

export const buildAdvancedRulesPayloadValidationPayload = (
  validationPayload: ModelValidationPayload,
  metaData: DetectionModelMetadata,
  irRule: IRSchema,
  irTemplate: DetectionModelTemplate | null,
  groupByType: DmObjectToFlag | 'HIT' | undefined,
  flagSingleObject?: boolean,
  isInternalRule?: boolean,
  trmSuppressAlerts?: boolean,
): ValidateModelPayload => {
  const startTime = getBEDateFormat(
    validationPayload.start_time,
    INPUT_FIELD_DATETIME_VALUE_FORMAT,
  );

  const endTime = getBEDateFormat(
    validationPayload.end_time,
    INPUT_FIELD_DATETIME_VALUE_FORMAT,
  );

  const objectGroups: Record<string, string> = {
    ENTITY: 'entity.id',
    INSTRUMENT: 'txn_instrument.id',
    DEVICE: 'device.id',
    HIT: 'hit.id',
  };

  const getGroupType = (groupType: string | undefined): string => {
    if (!groupType) {
      return 'entity.id';
    }
    return objectGroups[groupType];
  };

  return {
    tag_ids: metaData.tags,
    metadata: {
      title: metaData.title,
      description: metaData.description,
      decision: metaData.decision,
      parentRuleId: metaData.parentRuleId,
      customer_support_involved: false,
      exec_start_date_time: startTime,
      exec_end_date_time: endTime,
      execution_frequency_behavior:
        validationPayload.execution_frequency_behavior,
    },
    contents: {
      query_tree: {},
      inclusion_tags: [],
      inclusion_tag_names: [],
      exclusion_tags: [],
      exclusion_tag_names: [],
      scenario: {
        parameters: {
          $window_stride: validationPayload.execution_frequency,
          $time_window: getWindowSizeFromIR(irRule),
          alert_group_by_policy: groupByType,
          count_field: 'txn_event.id',
          group_type: getGroupType(groupByType),
          txn_events: 'transaction',
          flag_single_object: flagSingleObject,
          trm_suppress_alerts: trmSuppressAlerts,
        },
        filters: {},
        scenario_name: 'custom_scenario',
      },
      execution_frequency: validationPayload.execution_frequency,
      specification: irRule,
    },
    exec_range_start: startTime,
    exec_range_end: endTime,
    template_id: irTemplate?.id || undefined,
    runs_on_org_id: metaData.runs_on_org_id,
    visible_to_child_org: Boolean(metaData.visible_to_child_org),
    is_synchronous: flagSingleObject || false,
    is_internal_rule_recommendation: isInternalRule ?? false,
  };
};

export const getEditingAdvancedDetectionModelFromValidation = (
  detectionModelValidation: Pick<
    DetectionModelValidation,
    | 'content'
    | 'id'
    | 'title'
    | 'description'
    | 'tags'
    | 'exec_range_end'
    | 'exec_range_start'
    | 'execution_frequency'
    | 'execution_frequency_behavior'
    | 'customer_support_involved'
    | 'template'
    | 'queue'
    | 'runs_on_org_id'
    | 'visible_to_child_org'
    | 'decision'
  >,
): EditingAdvancedDetectionModel => {
  return cloneDeep({
    metadata: getModelMetadataFromValidation(detectionModelValidation),
    validationPayload: getValidationPayloadFromValidation(
      detectionModelValidation,
    ),
    ruleId: detectionModelValidation.id,
    irSchema: toIrSchemaOrNull(detectionModelValidation.content.specification),
    template: detectionModelValidation.template,
    trm_suppress_alerts:
      detectionModelValidation.content.scenario.parameters
        ?.trm_suppress_alerts ?? false,
  });
};

export const updateFact = (
  oldFact: DmEventByObjectFact | MultiplePerspectivesAggregation,
  selectedAggregateFunction: DmAggregateFunctions,
) => {
  const { aggregate_field: aggregateField } = oldFact;
  const updatedFact = {
    ...oldFact,
    aggregate_function: selectedAggregateFunction,
  };

  if (updatedFact.event_subtype === 'TXN_EVENT') {
    if (typeof aggregateField !== 'string') {
      updatedFact.aggregate_field =
        selectedAggregateFunction === 'COUNT'
          ? {
              ...aggregateField,
              field: 'id',
              datatype: 'text',
            }
          : {
              ...aggregateField,
              field: 'amount',
              datatype: 'number',
            };
    }
  } else if (updatedFact.event_subtype === 'ACTION_EVENT') {
    if (typeof aggregateField !== 'string') {
      if (selectedAggregateFunction === 'COUNT') {
        updatedFact.aggregate_field = {
          ...aggregateField,
          field: 'id',
          datatype: 'text',
        };
      } else if (selectedAggregateFunction === 'SUM') {
        updatedFact.aggregate_field = {
          ...aggregateField,
          field: 'amount',
          datatype: 'number',
        };
      }
    }
  }
  return updatedFact;
};

export const getMatchlistTypes = (
  scenarioName: string,
): Array<BlacklistType> => {
  switch (scenarioName) {
    case 'ip_blacklist':
    case 'ip_blacklist_embedded_filters':
      return ['IP_INET'];
    case 'blacklist_string':
    case 'entity_blacklist_string_embedded_filters':
    case 'event_blacklist_string_embedded_filters':
      return ['STRING'];
    case 'entity_blacklist':
    case 'entity_blacklist_embedded_filters':
      return ['USER', 'BUSINESS'];
    default:
      consoleError('Unknown scenario name: ', scenarioName);
      return [];
  }
};

export const renderScenarioWidgets = (
  scenarioConfig,
  selectedScenario,
  onChange,
  viewOnly = false,
) => {
  if (scenarioConfig.widgets) {
    const widgetProps: WidgetProps = {
      editingScenario: selectedScenario,
      viewOnly,
      onChange,
    };

    return scenarioConfig.widgets.map((widgetName) => {
      switch (widgetName as ScenarioBuilderWidgets) {
        case ScenarioConstants.BLACKLIST_SELECTOR:
          return (
            <ScenarioBlacklistSelectorNew key={widgetName} {...widgetProps} />
          );
        case ScenarioConstants.PER_FIELD_BLACKLIST_EDITOR:
          return (
            <ScenarioPerBlacklistEditor key={widgetName} {...widgetProps} />
          );
        case ScenarioConstants.SEQUENCE_CODE_EDITOR:
          return (
            <ScenarioSequenceCodeEditor key={widgetName} {...widgetProps} />
          );
        case ScenarioConstants.COMPONENT_RULES_SELECTOR:
          return <ComponentRulesSelector key={widgetName} {...widgetProps} />;
        case ScenarioConstants.LINK_ANALYSIS_MOCK:
          return <EntityLinkCountMockGraph key={widgetName} {...widgetProps} />;
        default:
          return <div key={widgetName}>Unknown Widget {widgetName}</div>;
      }
    });
  }
  return null;
};

export const getDmObjectFriendlyName = (
  dmObject: DmObjectToFlag | undefined,
): string => {
  if (!dmObject) {
    return '';
  }
  return dmObject.toLowerCase();
};

/**
 * Returns a prettified version of the time we save, 17w -> 17 weeks
 * @param time - string value representing time, like `17d` `1w` `1m`
 */
export const prettifyTime = (time): string => {
  if (!time) {
    return '';
  }

  // rare cases of 'fdom', 'ldom', 'fmom', etc
  const monthUnit = MONTH_UNIT_MAP[time];

  if (monthUnit) {
    return `${monthUnit}`;
  }

  // must be of a '18w' format
  try {
    const [, timeRange, timeUnit] = time.split(/(\d+)/);
    const formattedUnit = DURATION_UNIT_MAP[timeUnit].toLowerCase();

    if (Number(timeRange) === 1) {
      return `a(n) ${formattedUnit}`;
    }

    return `${timeRange} ${formattedUnit}s`;
  } catch {
    return '';
  }
};

export const convertDurationUnitToDays = (
  value: number,
  unit: DmDurationUnits,
) => {
  // Convert offset.value (in unit) → minutes, then divide by minutes per day
  return (value * TIME_CONVERSION[unit]) / TIME_CONVERSION.DAY;
};

export const generateConstantValue = (
  value: string | number | boolean | (string | number)[] | Date,
): ConstantValue => {
  return {
    type: ValueTypes.CONSTANT,
    value,
  };
};

export const PRETTY_OBJECT_MAP: Record<DmObjectToFlag, string> = {
  ENTITY: 'Entity',
  INSTRUMENT: 'Instrument',
  DEVICE: 'Device',
  TXN_EVENT: 'Transaction Event',
  ACTION_EVENT: 'Action Event',
};

export const isDmAggregateFunction = (
  aggregateFunction: DmAggregateFunctions | DropdownBuilderFormUiTypes,
): aggregateFunction is DmAggregateFunctions => {
  return (
    aggregateFunction === DmAggregateFunctions.AVERAGE ||
    aggregateFunction === DmAggregateFunctions.COUNT ||
    aggregateFunction === DmAggregateFunctions.MAXIMUM ||
    aggregateFunction === DmAggregateFunctions.MINIMUM ||
    aggregateFunction === DmAggregateFunctions.SUM
  );
};

export const isConditionalOperator = (
  operator?: DmOperator,
): operator is ConditionalOperators => {
  return operator !== undefined && Boolean(ConditionalOperators[operator]);
};

export const isCompositeOperator = (
  operator: DmOperator,
): operator is CompositeOperator => {
  return ['AND', 'OR'].includes(operator);
};

export const isCompositeQueryTreeNode = (
  node: QueryTreeNode,
): node is CompositeQueryTreeNode => node?.type === CompositeTypes.COMPOSITE;

export const isRuleConditionCompositeQueryTreeNode = (
  node: QueryTreeNode,
): node is CompositeQueryTreeNode =>
  node?.type === CompositeTypes.RULE_CONDITION_COMPOSITE;

export const isConditionOperand = (
  operand:
    | AndOrCondition
    | Condition
    | ConditionOperand
    | ConditionOperandValue
    | QueryTreeNode,
): operand is ConditionOperand => {
  if (operand) {
    return Object.hasOwn(operand, 'operandType');
  }
  return false;
};

export const filterOperatorFormOptions = (
  leftOperandValue: FieldValue | VariableValue,
  rightOperand: ConditionOperandValue,
  operator: DmOperator,
  symbolTable: SymbolTable,
  isRTM?: boolean,
): U21SelectOptionProps[] => {
  let operatorMapKey: string = 'text';
  let operandDataType: string;

  if (isFieldValue(leftOperandValue)) {
    const leftOperand: FieldValueOperand | null =
      getFieldValueOperandFromSymbolTable(leftOperandValue, symbolTable);

    if (leftOperand) {
      if (EMPTY_OPERATORS().has(operator)) {
        return OPERAND_TYPE_OPERATOR_OPTIONS_MAP.empty;
      }

      operandDataType = leftOperand.value.datatype; // NOTE: we use operand.value.datatype for legacy rules

      const rightOperandType = rightOperand?.type;
      operatorMapKey = operandDataType;
      if (['select', 'multiselect'].includes(operandDataType)) {
        operatorMapKey = 'list';
      } else if (operandDataType === 'date' || operandDataType === 'datetime') {
        if (rightOperand && rightOperandType === 'DURATION')
          return OPERAND_TYPE_OPERATOR_OPTIONS_MAP.number.filter(
            (option) =>
              option.value !== ConditionalOperators.BETWEEN &&
              option.value !== ConditionalOperators.IS,
          );
        return OPERAND_TYPE_OPERATOR_OPTIONS_MAP[operatorMapKey];
      }
    }
  } else if (isVariableValue(leftOperandValue)) {
    operatorMapKey = 'number';
    operandDataType = 'number';
  }

  if (operatorMapKey) {
    return OPERAND_TYPE_OPERATOR_OPTIONS_MAP[operatorMapKey].filter(
      (option) => {
        switch (option.value) {
          case ConditionalOperators.ILIKE_CONTAINS:
          case ConditionalOperators.NOT_ILIKE_CONTAINS:
          case ConditionalOperators.EQ:
          case ConditionalOperators.NEQ:
          case ConditionalOperators.LIKE:
          case ConditionalOperators.NOT_LIKE:
          case ConditionalOperators.IN:
          case ConditionalOperators.NOT_IN:
            return (
              operandDataType !== 'multiselect' || operatorMapKey === 'list'
            );
          case ConditionalOperators.ANY_EQ:
          case ConditionalOperators.NONE_EQ:
          case ConditionalOperators.ANY_IN:
          case ConditionalOperators.NONE_IN:
          case ConditionalOperators.ANY_LIKE:
          case ConditionalOperators.NONE_LIKE:
            return operandDataType === 'multiselect';
          case ConditionalOperators.GT:
          case ConditionalOperators.GE:
          case ConditionalOperators.LT:
          case ConditionalOperators.LE:
          case ConditionalOperators.BETWEEN:
            return operandDataType === 'number';
          case ConditionalOperators.STARTS_WITH:
          case ConditionalOperators.NOT_STARTS_WITH:
          case ConditionalOperators.ENDS_WITH:
          case ConditionalOperators.NOT_ENDS_WITH:
            return !isRTM;
          default:
            return false;
        }
      },
    );
  }
  return [];
};

export const MATCHLIST_COMPATIBLE_TYPES: string[] = [
  'string',
  'text',
  'enum',
  'list',
  'multiselect',
];

export const isListOperator = (operator: ConditionalOperators): boolean => {
  return IN_OPERATORS.has(operator);
};

export const USE_DATA_PATH: Set<string> = new Set([
  'business_name',
  'entity_type',
  'entity_subtype',
  'instrument_id',
]);
const USE_FIELD: Set<string> = new Set(['ip_address', 'client_fingerprint']);
const USE_BASE_MODEL: Set<string> = new Set(['ENTITY', 'INSTRUMENT']);
const USE_MODEL: Set<string> = new Set(['entity', 'txn_instrument']);

const generateNativeDataSymbols = (
  symbolTable: SymbolTable,
  builderType: 'dropdown' | 'advanced',
  baseModel: DmbQueryBuilderOption,
  model: DmbQueryBuilderOptionCode,
  { key: prefixKey, label: prefixLabel }: Prefix,
  nativeDataType: OperandTypes,
  dropdownValues: U21SelectOptionProps[],
  dataSetting: NativeDataSettingsConfigResponse,
): SymbolTable => {
  const newSymbolTable = symbolTable;
  const dataPath = rectifyNativeDataSettingKeyIfApplicable(dataSetting);

  const shouldAddModelPrefix: boolean = !(
    (prefixKey === 'txn_instrument' && baseModel === 'INSTRUMENT') ||
    prefixKey === baseModel.toLowerCase()
  );
  const shouldAddTxnDirectionPrefix: boolean =
    /(sender|receiver|perspective)_(entity|instrument)/gi.test(prefixKey);
  const shouldAddPrefix = shouldAddModelPrefix || shouldAddTxnDirectionPrefix;

  const {
    unit21_data_classifier: unit21DataClassifier,
    native_key: nativeKey,
    description: dataSettingDescription,
  } = dataSetting;

  let text = keyPathToLabel(dataSetting, false, ' / ');
  if (shouldAddPrefix) {
    text = `${prefixLabel[0].toUpperCase() + prefixLabel.slice(1)} / ${text}`;
  } else if (unit21DataClassifier === Unit21DataClassifier.ACTION_EVENT) {
    text = `Action / ${text}`;
  } else if (unit21DataClassifier === Unit21DataClassifier.EVENT) {
    text = `Transaction / ${text}`;
  } else if (unit21DataClassifier === Unit21DataClassifier.ENTITY) {
    text = `Entity / ${text}`;
  } else if (unit21DataClassifier === Unit21DataClassifier.INSTRUMENT) {
    text = `Instrument / ${text}`;
  }

  let modelOrBaseModel: string = model;
  let field = `${shouldAddPrefix ? `${prefixKey}.` : ''}${dataPath}`;
  if (USE_BASE_MODEL.has(baseModel)) {
    field = USE_DATA_PATH.has(nativeKey) ? dataPath : nativeKey;
    if (!USE_MODEL.has(model)) {
      field = `${prefixKey}.${field}`;
      modelOrBaseModel = baseModel.toLocaleLowerCase(); // NOTE: this is for cases like fieldvalue where field is `txn_event.address.city`, the model still needs to be `txn_event` and not `address`
    }
  }
  const nativeKeyOrField = USE_FIELD.has(nativeKey) ? field : nativeKey;
  let symbolKey = `${shouldAddPrefix ? `${prefixKey}.` : ''}${nativeKeyOrField}`;
  let symbolValue: SymbolFieldValue | OperandSymbol;

  if (builderType === 'dropdown') {
    symbolValue = {
      type: nativeDataType,
      text,
      value: {
        operandType: dropdownValues.length ? 'multiselect' : nativeDataType,
        unit21DataClassifier,
        value: {
          type: ValueTypes.FIELD,
          datatype: nativeDataType === 'text' ? 'string' : nativeDataType,
          model: modelOrBaseModel,
          field,
          label: symbolKey, // used to find symbol
        },
        dropdownValues,
      },
      description: dataSettingDescription
        ? truncate(dataSettingDescription, {
            length: MAX_DROPDOWN_OPTION_DESCRIPTION_LENGTH,
          })
        : undefined,
    };
  } else {
    // Advanced builder logic
    symbolKey = `${shouldAddModelPrefix ? `${prefixKey}.` : ''}${dataPath}`;
    symbolValue = {
      type: ValueTypes.FIELD,
      datatype: nativeDataType === 'text' ? 'string' : nativeDataType,
      model: modelOrBaseModel,
      field,
      label: text, // used as rendered text in code mirror
    };
  }

  newSymbolTable[symbolKey] = symbolValue;
  return newSymbolTable;
};

const FIELDS_NOT_SUPPORTED_IN_DM = [
  'address',
  'addresses',
  'client_fingerprints',
  'email_addresses',
  'ip_addresses',
  'phone_numbers',
  'physical_ids',
];

const SUPPORTED_DATA_CLASSIFIER: ReadonlySet<Unit21DataClassifier> = new Set([
  Unit21DataClassifier.ACTION_EVENT,
  Unit21DataClassifier.ADDRESS,
  Unit21DataClassifier.EVENT,
  Unit21DataClassifier.ENTITY,
  Unit21DataClassifier.INSTRUMENT,
]);

const PRIMITIVE_TYPES_NOT_SUPPORTED_IN_DM: Set<DataSettingFieldType> = new Set([
  DataSettingFieldType.JSON,
]);

export const CLASSIFIER_SECTION_MAP: Record<
  Exclude<
    Unit21DataClassifier,
    | Unit21DataClassifier.ALERT
    | Unit21DataClassifier.CASE
    | Unit21DataClassifier.CLIENT_FINGERPRINT
    | Unit21DataClassifier.EMAIL_ADDRESS
    | Unit21DataClassifier.IP_ADDRESS
    | Unit21DataClassifier.PHONE_NUMBER
    | Unit21DataClassifier.PHYSICAL_ID
    | Unit21DataClassifier.RULE
    | Unit21DataClassifier.WATCHLIST
    | Unit21DataClassifier.TRM_RISK_INDICATOR
    | Unit21DataClassifier.TRM_EXPOSURE_PATH
    | Unit21DataClassifier.TRM_ENTITY
    | Unit21DataClassifier.TRM_ADDRESS_BEHAVIORAL_SIGNATURE
    | Unit21DataClassifier.ALL
  >,
  DmbQueryBuilderOptionCode
> = {
  [Unit21DataClassifier.ACTION_EVENT]: 'action_event',
  [Unit21DataClassifier.ADDRESS]: 'address',
  [Unit21DataClassifier.INSTRUMENT]: 'txn_instrument',
  [Unit21DataClassifier.EVENT]: 'txn_event',
  [Unit21DataClassifier.ENTITY]: 'entity',
};

export const CLASSIFIER_SECTION_MAP_FRIENDLY_NAME: Record<
  Exclude<
    Unit21DataClassifier,
    | Unit21DataClassifier.ALERT
    | Unit21DataClassifier.CASE
    | Unit21DataClassifier.CLIENT_FINGERPRINT
    | Unit21DataClassifier.EMAIL_ADDRESS
    | Unit21DataClassifier.IP_ADDRESS
    | Unit21DataClassifier.PHONE_NUMBER
    | Unit21DataClassifier.PHYSICAL_ID
    | Unit21DataClassifier.RULE
    | Unit21DataClassifier.WATCHLIST
    | Unit21DataClassifier.TRM_RISK_INDICATOR
    | Unit21DataClassifier.TRM_EXPOSURE_PATH
    | Unit21DataClassifier.TRM_ENTITY
    | Unit21DataClassifier.TRM_ADDRESS_BEHAVIORAL_SIGNATURE
    | Unit21DataClassifier.ALL
  >,
  DmbQueryBuilderOptionCodeFriendlyName
> = {
  [Unit21DataClassifier.ACTION_EVENT]: 'Action event',
  [Unit21DataClassifier.ADDRESS]: 'Address',
  [Unit21DataClassifier.INSTRUMENT]: 'Instrument',
  [Unit21DataClassifier.EVENT]: 'Transaction event',
  [Unit21DataClassifier.ENTITY]: 'Entity',
};

const rectifyNativeDataSettingKeyIfApplicable = (
  dataSetting: NativeDataSettingsConfigResponse,
): string => {
  let overrideField =
    NATIVE_DATA_SETTINGS_NATIVE_KEY_MISMATCH_MAP[
      dataSetting.unit21_data_classifier
    ][dataSetting.native_key];
  if (dataSetting.native_key === 'ip_address') {
    // NOTE: ip_address condition is unique to DMB context
    overrideField = 'ip_address.ip_address';
  }
  if (dataSetting.native_key === 'client_fingerprint') {
    // NOTE: client_fingerprint condition is unique to DMB context
    overrideField = 'client_fingerprint.client_fingerprint';
  }
  return overrideField || dataSetting.native_key;
};

const SENDER_RECEIVER = ['sender', 'receiver'];

const filterFocsecFieldsIfDisabled = (
  shouldUseFocsecFields: boolean,
  key: string,
): boolean => {
  if (shouldUseFocsecFields) {
    return true;
  }
  return !['ip_is_vpn', 'ip_is_proxy', 'ip_is_tor', 'ip_is_bot'].includes(key);
};

export const REACH_OUT_TEXT =
  'Please reach out to your Customer Success Manager, or';

export const RISK_RATINGS_TOOLTIP = (
  <p>
    Customer Risk Ratings must be enabled to use this feature. {REACH_OUT_TEXT}{' '}
    <SupportRequestLink /> to our Support team to learn more.
  </p>
);

export const mergeSymbolTableWithNativeData = (
  symbolTable: SymbolTable,
  nativeData: NativeDataSettingsConfigResponse[],
  baseModel: DmbQueryBuilderOption,
  builderType: 'dropdown' | 'advanced',
  shouldUseFocsecFields: boolean,
  isRiskRatingsEnabled: boolean,
  hasActiveRiskRatingModel: boolean,
  shouldUseTrmFields: boolean = false,
  shouldUseTrmEntityFields: boolean = false,
): SymbolTable => {
  let newSymbolTable: SymbolTable | undefined;
  nativeData
    .filter((dataSetting) => {
      // NOTE: native data fields that DMB doesn't support (yet?)
      return (
        // certain fields are not supported in DMB
        !FIELDS_NOT_SUPPORTED_IN_DM.includes(dataSetting.native_key) &&
        // certain primitive types are not supported in DMB
        !PRIMITIVE_TYPES_NOT_SUPPORTED_IN_DM.has(dataSetting.data_type) &&
        // only certain data classifiers are supported in DMB
        SUPPORTED_DATA_CLASSIFIER.has(dataSetting.unit21_data_classifier) &&
        (dataSetting.unit21_data_classifier !==
          Unit21DataClassifier.INSTRUMENT ||
          !dataSetting.native_key.startsWith('fingerprint_')) &&
        // TODO sc-112728: delete filtering out geolocation/focsec/risk native data settings once backfill is complete
        !dataSetting.native_key.startsWith('geolocation_') &&
        !dataSetting.native_key.startsWith('focsec_') &&
        !dataSetting.native_key.startsWith('u21_latest_risk_') &&
        !dataSetting.native_key.startsWith('trm_')
      );
    })
    .forEach((dataSetting) => {
      const {
        unit21_data_classifier: unit21DataClassifier,
        data_type: dataType,
        enum_set: enumSet,
      } = dataSetting;
      const lowerCasedClassifier: string = unit21DataClassifier.toLowerCase();
      const nativeDataType = DATATYPE_MAP[dataType] || 'text';

      let dropdownValues: U21SelectOptionProps[] = [];
      if (dataType === DataSettingFieldType.ENUM && enumSet) {
        dropdownValues = enumSet.map((enumValue) => {
          return {
            text: enumValue,
            value: enumValue,
          };
        });
      }

      if (baseModel === 'TXN_EVENT') {
        if (
          unit21DataClassifier === Unit21DataClassifier.ENTITY ||
          unit21DataClassifier === Unit21DataClassifier.INSTRUMENT
        ) {
          SENDER_RECEIVER.forEach((direction) => {
            newSymbolTable = generateNativeDataSymbols(
              symbolTable,
              builderType,
              baseModel,
              DM_OBJECT_ENUM_TO_CODE_MAP[baseModel],
              {
                key: `${direction}_${lowerCasedClassifier}`,
                label: `${CLASSIFIER_SECTION_MAP_FRIENDLY_NAME[unit21DataClassifier]} / ${toSentenceCase(direction)} ${lowerCasedClassifier}`,
              },
              nativeDataType,
              dropdownValues,
              dataSetting,
            );
          });
        } else if (unit21DataClassifier === Unit21DataClassifier.ADDRESS) {
          // note: txn_event.address fields
          newSymbolTable = generateNativeDataSymbols(
            symbolTable,
            builderType,
            baseModel,
            DM_OBJECT_ENUM_TO_CODE_MAP[baseModel],
            {
              key: CLASSIFIER_SECTION_MAP[unit21DataClassifier],
              label: CLASSIFIER_SECTION_MAP_FRIENDLY_NAME[unit21DataClassifier],
            },
            nativeDataType,
            dropdownValues,
            dataSetting,
          );

          SENDER_RECEIVER.forEach((direction) => {
            if (newSymbolTable) {
              // NOTE: {sender/receiver}_entity.address fields
              newSymbolTable = generateNativeDataSymbols(
                newSymbolTable,
                builderType,
                baseModel,
                DM_OBJECT_ENUM_TO_CODE_MAP[baseModel],
                {
                  key: `${direction}_entity.address`,
                  label: `Entity / ${toSentenceCase(direction)} entity / Address`,
                },
                nativeDataType,
                dropdownValues,
                dataSetting,
              );
            }
          });
          SENDER_RECEIVER.forEach((direction) => {
            if (newSymbolTable) {
              // NOTE: {sender/receiver}_instrument.address fields
              newSymbolTable = generateNativeDataSymbols(
                newSymbolTable,
                builderType,
                baseModel,
                DM_OBJECT_ENUM_TO_CODE_MAP[baseModel],
                {
                  key: `${direction}_instrument.address`,
                  label: `Instrument / ${toSentenceCase(direction)} instrument / Address`,
                },
                nativeDataType,
                dropdownValues,
                dataSetting,
              );
            }
          });
        } else if (unit21DataClassifier === Unit21DataClassifier.EVENT) {
          newSymbolTable = generateNativeDataSymbols(
            symbolTable,
            builderType,
            baseModel,
            CLASSIFIER_SECTION_MAP[unit21DataClassifier],
            {
              key: CLASSIFIER_SECTION_MAP[unit21DataClassifier],
              label: CLASSIFIER_SECTION_MAP_FRIENDLY_NAME[unit21DataClassifier],
            },
            nativeDataType,
            dropdownValues,
            dataSetting,
          );
        }
      } else if (baseModel === Unit21DataClassifier.ACTION_EVENT) {
        if (
          unit21DataClassifier === Unit21DataClassifier.ENTITY ||
          unit21DataClassifier === Unit21DataClassifier.INSTRUMENT
        ) {
          newSymbolTable = generateNativeDataSymbols(
            symbolTable,
            builderType,
            baseModel,
            DM_OBJECT_ENUM_TO_CODE_MAP[baseModel],
            {
              key: CLASSIFIER_SECTION_MAP[unit21DataClassifier],
              label: CLASSIFIER_SECTION_MAP_FRIENDLY_NAME[unit21DataClassifier],
            },
            nativeDataType,
            dropdownValues,
            dataSetting,
          );
        } else if (unit21DataClassifier === Unit21DataClassifier.ADDRESS) {
          // NOTE: action_event.address fields
          newSymbolTable = generateNativeDataSymbols(
            symbolTable,
            builderType,
            baseModel,
            DM_OBJECT_ENUM_TO_CODE_MAP[baseModel],
            {
              key: CLASSIFIER_SECTION_MAP[unit21DataClassifier],
              label: CLASSIFIER_SECTION_MAP_FRIENDLY_NAME[unit21DataClassifier],
            },
            nativeDataType,
            dropdownValues,
            dataSetting,
          );
          ['entity', 'txn_instrument'].forEach((dmObjectType) => {
            // NOTE: action_event.entity/txn_instrument.address fields
            const dmObjectTypeLabel =
              dmObjectType === 'entity' ? 'Entity' : 'Instrument';
            if (newSymbolTable) {
              newSymbolTable = generateNativeDataSymbols(
                newSymbolTable,
                builderType,
                baseModel,
                DM_OBJECT_ENUM_TO_CODE_MAP[baseModel],
                {
                  key: `${dmObjectType}.address`,
                  label: `${dmObjectTypeLabel} / Address`,
                },
                nativeDataType,
                dropdownValues,
                dataSetting,
              );
            }
          });
        } else if (unit21DataClassifier === Unit21DataClassifier.ACTION_EVENT) {
          newSymbolTable = generateNativeDataSymbols(
            symbolTable,
            builderType,
            baseModel,
            CLASSIFIER_SECTION_MAP[unit21DataClassifier],
            {
              key: CLASSIFIER_SECTION_MAP[unit21DataClassifier],
              label: CLASSIFIER_SECTION_MAP_FRIENDLY_NAME[unit21DataClassifier],
            },
            nativeDataType,
            dropdownValues,
            dataSetting,
          );
        }
      } else if (
        ((baseModel === Unit21DataClassifier.ENTITY ||
          // we need entity symbols for consortium rules
          baseModel === 'CONSORTIUM') &&
          (unit21DataClassifier === Unit21DataClassifier.ADDRESS ||
            unit21DataClassifier === Unit21DataClassifier.ENTITY)) ||
        (baseModel === Unit21DataClassifier.INSTRUMENT &&
          (unit21DataClassifier === Unit21DataClassifier.ADDRESS ||
            unit21DataClassifier === Unit21DataClassifier.INSTRUMENT))
      ) {
        newSymbolTable = generateNativeDataSymbols(
          symbolTable,
          builderType,
          baseModel,
          CLASSIFIER_SECTION_MAP[unit21DataClassifier],
          {
            key: CLASSIFIER_SECTION_MAP[unit21DataClassifier],
            label: CLASSIFIER_SECTION_MAP_FRIENDLY_NAME[unit21DataClassifier],
          },
          nativeDataType,
          dropdownValues,
          dataSetting,
        );
      }
    });

  // NOTE: [SC-62244]. hard coded entries to support legacy code, need to coordinate with DIM team to support these, then remove this block of code
  // sender_instrument.external_id, receiver_instrument.external_id (model txn_event)
  const oldOrNewSymbolTable = newSymbolTable || symbolTable;
  if (baseModel === 'ENTITY') {
    if (builderType === 'advanced') {
      if (isRiskRatingsEnabled || hasActiveRiskRatingModel) {
        addLatestRiskFieldsToEventForAdvancedBuilder(
          oldOrNewSymbolTable,
          null,
          Unit21DataClassifier.ENTITY,
        );
      }
      oldOrNewSymbolTable.cumulative_transaction_sum = {
        type: ValueTypes.FIELD,
        field: 'cumulative_transaction_sum',
        label: 'Cumulative transaction sum',
        model: 'entity',
        datatype: 'number',
        cardinality: 'many',
      };
      oldOrNewSymbolTable.first_transaction = {
        type: ValueTypes.FIELD,
        field: 'first_transaction',
        label: 'First transaction',
        model: 'entity',
        datatype: 'datetime',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['client_fingerprint.client_fingerprint'] = {
        type: ValueTypes.FIELD,
        field: 'client_fingerprint.client_fingerprint',
        label: 'Client fingerprint',
        model: 'entity',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['ip_address.ip_address'] = {
        type: ValueTypes.FIELD,
        field: 'ip_address.ip_address',
        label: 'IP address',
        model: 'entity',
        datatype: 'string',
        cardinality: 'many',
      };
      if (shouldUseFocsecFields) {
        oldOrNewSymbolTable['ip_address.ip_is_bot'] = {
          type: ValueTypes.FIELD,
          field: 'ip_address.ip_is_bot',
          label: 'IP is bot',
          model: 'entity',
          datatype: 'boolean',
          cardinality: 'many',
        };
        oldOrNewSymbolTable['ip_address.ip_is_vpn'] = {
          type: ValueTypes.FIELD,
          field: 'ip_address.ip_is_vpn',
          label: 'IP uses VPN',
          model: 'entity',
          datatype: 'boolean',
          cardinality: 'many',
        };
        oldOrNewSymbolTable['ip_address.ip_is_proxy'] = {
          type: ValueTypes.FIELD,
          field: 'ip_address.ip_is_proxy',
          label: 'IP uses proxy',
          model: 'entity',
          datatype: 'boolean',
          cardinality: 'many',
        };
        oldOrNewSymbolTable['ip_address.ip_is_tor'] = {
          type: ValueTypes.FIELD,
          field: 'ip_address.ip_is_tor',
          label: 'IP uses Tor',
          model: 'entity',
          datatype: 'boolean',
          cardinality: 'many',
        };
      }
      oldOrNewSymbolTable['phone_number.number'] = {
        type: ValueTypes.FIELD,
        field: 'phone_number.number',
        label: 'Phone number / Number',
        model: 'entity',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['email_address.email_address'] = {
        type: ValueTypes.FIELD,
        field: 'email_address.email_address',
        label: 'Email address',
        model: 'entity',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable.risk_score = {
        // Although risk score is deprecated, keep the symbol to ensure old rules  displayed correctly.
        type: ValueTypes.FIELD,
        field: 'risk_score',
        label: 'Risk score',
        model: 'entity',
        datatype: 'number',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['geolocation.country'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.country',
        label: 'Geolocation / Country',
        model: 'entity',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['geolocation.city'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.city',
        label: 'Geolocation / City',
        model: 'entity',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['geolocation.postal_code'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.postal_code',
        label: 'Geolocation / Postal code',
        model: 'entity',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['geolocation.longitude'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.longitude',
        label: 'Geolocation / Longitude',
        model: 'entity',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['geolocation.latitude'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.latitude',
        label: 'Geolocation / Latitude',
        model: 'entity',
        datatype: 'string',
        cardinality: 'many',
      };
    } else {
      oldOrNewSymbolTable.cumulative_transaction_sum = {
        type: OperandTypes.TEXT,
        text: 'Cumulative transaction sum',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.ENTITY,
          value: {
            type: ValueTypes.FIELD,
            field: 'cumulative_transaction_sum',
            label: 'cumulative_transaction_sum',
            model: 'entity',
            datatype: 'number',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable.first_transaction = {
        type: OperandTypes.TEXT,
        text: 'First transaction',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.ENTITY,
          value: {
            type: ValueTypes.FIELD,
            field: 'first_transaction',
            label: 'first_transaction',
            model: 'entity',
            datatype: 'datetime',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['phone_number.number'] = {
        type: OperandTypes.TEXT,
        text: 'Phone number / Number',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.ENTITY,
          value: {
            type: ValueTypes.FIELD,
            field: 'phone_number.number',
            label: 'phone_number.number',
            model: 'entity',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['client_fingerprint.client_fingerprint'] = {
        type: OperandTypes.TEXT,
        text: 'Client fingerprint',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.ENTITY,
          value: {
            type: ValueTypes.FIELD,
            field: 'client_fingerprint.client_fingerprint',
            label: 'client_fingerprint.client_fingerprint',
            model: 'entity',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['email_address.email_address'] = {
        type: OperandTypes.TEXT,
        text: 'Email address',
        value: {
          operandType: 'text',
          unit21DataClassifier: Unit21DataClassifier.ENTITY,
          value: {
            type: ValueTypes.FIELD,
            field: 'email_address.email_address',
            label: 'email_address.email_address',
            model: 'entity',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['geolocation.country'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / Country',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.ENTITY,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.country',
            label: 'geolocation.country',
            model: 'entity',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['geolocation.city'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / City',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.ENTITY,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.city',
            label: 'geolocation.city',
            model: 'entity',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['geolocation.postal_code'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / Postal code',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.ENTITY,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.postal_code',
            label: 'geolocation.postal_code',
            model: 'entity',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['geolocation.longitude'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / Longitude',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.ENTITY,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.longitude',
            label: 'geolocation.longitude',
            model: 'entity',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['geolocation.latitude'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / Latitude',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.ENTITY,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.latitude',
            label: 'geolocation.latitude',
            model: 'entity',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      if (shouldUseFocsecFields) {
        oldOrNewSymbolTable['ip_address.ip_is_bot'] = {
          type: OperandTypes.TEXT,
          text: 'IP uses bot',
          value: {
            operandType: OperandTypes.BOOLEAN,
            unit21DataClassifier: Unit21DataClassifier.ENTITY,
            value: {
              type: ValueTypes.FIELD,
              field: 'ip_address.ip_is_bot',
              label: 'IP uses bot',
              model: 'entity',
              datatype: 'boolean',
              cardinality: 'many',
            },
          },
        };
        oldOrNewSymbolTable['ip_address.ip_is_vpn'] = {
          type: OperandTypes.TEXT,
          text: 'IP uses VPN',
          value: {
            operandType: OperandTypes.BOOLEAN,
            unit21DataClassifier: Unit21DataClassifier.ENTITY,
            value: {
              type: ValueTypes.FIELD,
              field: 'ip_address.ip_is_vpn',
              label: 'IP uses VPN',
              model: 'entity',
              datatype: 'boolean',
              cardinality: 'many',
            },
          },
        };
        oldOrNewSymbolTable['ip_address.ip_is_proxy'] = {
          type: OperandTypes.TEXT,
          text: 'IP uses proxy',
          value: {
            operandType: OperandTypes.BOOLEAN,
            unit21DataClassifier: Unit21DataClassifier.ENTITY,
            value: {
              type: ValueTypes.FIELD,
              field: 'ip_address.ip_is_proxy',
              label: 'IP uses proxy',
              model: 'entity',
              datatype: 'boolean',
              cardinality: 'many',
            },
          },
        };
        oldOrNewSymbolTable['ip_address.ip_is_tor'] = {
          type: OperandTypes.TEXT,
          text: 'IP uses Tor',
          value: {
            operandType: OperandTypes.BOOLEAN,
            unit21DataClassifier: Unit21DataClassifier.ENTITY,
            value: {
              type: ValueTypes.FIELD,
              field: 'ip_address.ip_is_tor',
              label: 'IP uses Tor',
              model: 'entity',
              datatype: 'boolean',
              cardinality: 'many',
            },
          },
        };
      }
      oldOrNewSymbolTable['ip_address.ip_address'] = {
        type: OperandTypes.TEXT,
        text: 'IP address',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.ENTITY,
          value: {
            type: ValueTypes.FIELD,
            field: 'ip_address.ip_address',
            label: 'IP address',
            model: 'entity',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      addLatestRiskFieldsToEventForDropdownBuilder(
        oldOrNewSymbolTable,
        null,
        Unit21DataClassifier.ENTITY,
        isRiskRatingsEnabled,
        hasActiveRiskRatingModel,
      );
    }
  }
  if (baseModel === 'INSTRUMENT') {
    if (builderType === 'advanced') {
      oldOrNewSymbolTable.gateway = {
        type: ValueTypes.FIELD,
        field: 'gateway',
        label: 'Gateway',
        model: 'txn_instrument',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable.parent_id = {
        type: ValueTypes.FIELD,
        field: 'parent_id',
        label: 'Parent ID',
        model: 'txn_instrument',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['client_fingerprint.client_fingerprint'] = {
        type: ValueTypes.FIELD,
        field: 'client_fingerprint.client_fingerprint',
        label: 'Client fingerprint',
        model: 'txn_instrument',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['ip_address.ip_address'] = {
        type: ValueTypes.FIELD,
        field: 'ip_address.ip_address',
        label: 'IP address',
        model: 'txn_instrument',
        datatype: 'string',
        cardinality: 'many',
      };
      if (shouldUseFocsecFields) {
        oldOrNewSymbolTable['ip_address.ip_is_bot'] = {
          type: ValueTypes.FIELD,
          field: 'ip_address.ip_is_bot',
          label: 'IP uses bot',
          model: 'txn_instrument',
          datatype: 'boolean',
          cardinality: 'many',
        };
        oldOrNewSymbolTable['ip_address.ip_is_vpn'] = {
          type: ValueTypes.FIELD,
          field: 'ip_address.ip_is_vpn',
          label: 'IP uses VPN',
          model: 'txn_instrument',
          datatype: 'boolean',
          cardinality: 'many',
        };
        oldOrNewSymbolTable['ip_address.ip_is_proxy'] = {
          type: ValueTypes.FIELD,
          field: 'ip_address.ip_is_proxy',
          label: 'IP uses proxy',
          model: 'txn_instrument',
          datatype: 'boolean',
          cardinality: 'many',
        };
        oldOrNewSymbolTable['ip_address.ip_is_tor'] = {
          type: ValueTypes.FIELD,
          field: 'ip_address.ip_is_tor',
          label: 'IP uses Tor',
          model: 'txn_instrument',
          datatype: 'boolean',
          cardinality: 'many',
        };
      }
      if (shouldUseTrmFields) {
        Object.assign(
          oldOrNewSymbolTable,
          buildTrmFieldsForAdvancedBuilder(
            undefined,
            'txn_instrument',
            shouldUseTrmEntityFields,
          ),
        );
      }
      oldOrNewSymbolTable['geolocation.country'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.country',
        label: 'Geolocation / Country',
        model: 'txn_instrument',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['geolocation.city'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.city',
        label: 'Geolocation / City',
        model: 'txn_instrument',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['geolocation.postal_code'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.postal_code',
        label: 'Geolocation / Postal code',
        model: 'txn_instrument',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['geolocation.longitude'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.longitude',
        label: 'Geolocation / Longitude',
        model: 'txn_instrument',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['geolocation.latitude'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.latitude',
        label: 'Geolocation / Latitude',
        model: 'txn_instrument',
        datatype: 'string',
        cardinality: 'many',
      };
    } else {
      oldOrNewSymbolTable.gateway = {
        type: OperandTypes.TEXT,
        text: 'Gateway',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.INSTRUMENT,
          value: {
            type: ValueTypes.FIELD,
            field: 'gateway',
            label: 'gateway',
            model: 'txn_instrument',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable.parent_id = {
        type: OperandTypes.TEXT,
        text: 'Parent ID',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.INSTRUMENT,
          value: {
            type: ValueTypes.FIELD,
            field: 'parent_id',
            label: 'parent_id',
            model: 'txn_instrument',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['client_fingerprint.client_fingerprint'] = {
        type: OperandTypes.TEXT,
        text: 'Client fingerprint',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.INSTRUMENT,
          value: {
            type: ValueTypes.FIELD,
            field: 'client_fingerprint.client_fingerprint',
            label: 'client_fingerprint.client_fingerprint',
            model: 'txn_instrument',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['ip_address.ip_address'] = {
        type: OperandTypes.TEXT,
        text: 'IP address',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.INSTRUMENT,
          value: {
            type: ValueTypes.FIELD,
            field: 'ip_address.ip_address',
            label: 'IP address',
            model: 'txn_instrument',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      if (shouldUseFocsecFields) {
        oldOrNewSymbolTable['ip_address.ip_is_bot'] = {
          type: OperandTypes.TEXT,
          text: 'IP uses bot',
          value: {
            operandType: OperandTypes.BOOLEAN,
            unit21DataClassifier: Unit21DataClassifier.INSTRUMENT,
            value: {
              type: ValueTypes.FIELD,
              field: 'ip_address.ip_is_bot',
              label: 'IP uses bot',
              model: 'txn_instrument',
              datatype: 'boolean',
              cardinality: 'many',
            },
          },
        };
        oldOrNewSymbolTable['ip_address.ip_is_vpn'] = {
          type: OperandTypes.TEXT,
          text: 'IP uses VPN',
          value: {
            operandType: OperandTypes.BOOLEAN,
            unit21DataClassifier: Unit21DataClassifier.INSTRUMENT,
            value: {
              type: ValueTypes.FIELD,
              field: 'ip_address.ip_is_vpn',
              label: 'IP uses VPN',
              model: 'txn_instrument',
              datatype: 'boolean',
              cardinality: 'many',
            },
          },
        };
        oldOrNewSymbolTable['ip_address.ip_is_proxy'] = {
          type: OperandTypes.TEXT,
          text: 'IP uses proxy',
          value: {
            operandType: OperandTypes.BOOLEAN,
            unit21DataClassifier: Unit21DataClassifier.INSTRUMENT,
            value: {
              type: ValueTypes.FIELD,
              field: 'ip_address.ip_is_proxy',
              label: 'IP uses proxy',
              model: 'txn_instrument',
              datatype: 'boolean',
              cardinality: 'many',
            },
          },
        };
        oldOrNewSymbolTable['ip_address.ip_is_tor'] = {
          type: OperandTypes.TEXT,
          text: 'IP uses tor',
          value: {
            operandType: OperandTypes.BOOLEAN,
            unit21DataClassifier: Unit21DataClassifier.INSTRUMENT,
            value: {
              type: ValueTypes.FIELD,
              field: 'ip_address.ip_is_tor',
              label: 'IP uses Tor',
              model: 'txn_instrument',
              datatype: 'boolean',
              cardinality: 'many',
            },
          },
        };
      }
      if (shouldUseTrmFields) {
        Object.assign(
          oldOrNewSymbolTable,
          buildTrmFieldsForDropdownBuilder(
            Unit21DataClassifier.INSTRUMENT,
            undefined,
            'txn_instrument',
            shouldUseTrmEntityFields,
          ),
        );
      }
      oldOrNewSymbolTable['geolocation.country'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / Country',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.INSTRUMENT,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.country',
            label: 'geolocation.country',
            model: 'txn_instrument',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['geolocation.city'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / City',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.INSTRUMENT,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.city',
            label: 'geolocation.city',
            model: 'txn_instrument',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['geolocation.postal_code'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / Postal code',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.INSTRUMENT,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.postal_code',
            label: 'geolocation.postal_code',
            model: 'txn_instrument',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['geolocation.longitude'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / Longitude',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.INSTRUMENT,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.longitude',
            label: 'geolocation.longitude',
            model: 'txn_instrument',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['geolocation.latitude'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / Latitude',
        value: {
          operandType: OperandTypes.TEXT,
          unit21DataClassifier: Unit21DataClassifier.INSTRUMENT,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.latitude',
            label: 'geolocation.latitude',
            model: 'txn_instrument',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
    }
  }
  if (baseModel === 'DEVICE') {
    if (builderType === 'advanced') {
      oldOrNewSymbolTable.os_name = {
        type: ValueTypes.FIELD,
        field: 'os_name',
        label: 'OS name',
        model: 'device',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable.network_carrier = {
        type: ValueTypes.FIELD,
        field: 'network_carrier',
        label: 'Network carrier',
        model: 'device',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable.network_cellular = {
        type: ValueTypes.FIELD,
        field: 'network_cellular',
        label: 'Network cellular',
        model: 'device',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable.model = {
        type: ValueTypes.FIELD,
        field: 'model',
        label: 'Model',
        model: 'device',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable.manufacturer = {
        type: ValueTypes.FIELD,
        field: 'manufacturer',
        label: 'Manufacturer',
        model: 'device',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable.app_version = {
        type: ValueTypes.FIELD,
        field: 'app_version',
        label: 'App version',
        model: 'device',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['ip_address.ip_address'] = {
        type: ValueTypes.FIELD,
        field: 'ip_address.ip_address',
        label: 'IP address',
        model: 'device',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['phone_number.number'] = {
        type: ValueTypes.FIELD,
        field: 'phone_number.number',
        label: 'Phone number / Number',
        model: 'device',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['email_address.email_address'] = {
        type: ValueTypes.FIELD,
        field: 'email_address.email_address',
        label: 'Email address',
        model: 'device',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['geolocation.country'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.country',
        label: 'Geolocation / Country',
        model: 'device',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['geolocation.city'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.city',
        label: 'Geolocation / City',
        model: 'device',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['geolocation.postal_code'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.postal_code',
        label: 'Geolocation / Postal code',
        model: 'device',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['geolocation.longitude'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.longitude',
        label: 'Geolocation / Longitude',
        model: 'device',
        datatype: 'string',
        cardinality: 'many',
      };
      oldOrNewSymbolTable['geolocation.latitude'] = {
        type: ValueTypes.FIELD,
        field: 'geolocation.latitude',
        label: 'Geolocation / Latitude',
        model: 'device',
        datatype: 'string',
        cardinality: 'many',
      };
    } else {
      oldOrNewSymbolTable.os_name = {
        type: OperandTypes.TEXT,
        text: 'OS name',
        value: {
          operandType: OperandTypes.TEXT,
          value: {
            type: ValueTypes.FIELD,
            field: 'os_name',
            label: 'os_name',
            model: 'device',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable.network_carrier = {
        type: OperandTypes.TEXT,
        text: 'Network carrier',
        value: {
          operandType: OperandTypes.TEXT,
          value: {
            type: ValueTypes.FIELD,
            field: 'network_carrier',
            label: 'network_carrier',
            model: 'device',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable.network_cellular = {
        type: OperandTypes.TEXT,
        text: 'Network cellular',
        value: {
          operandType: OperandTypes.TEXT,
          value: {
            type: ValueTypes.FIELD,
            field: 'network_cellular',
            label: 'network_cellular',
            model: 'device',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable.model = {
        type: OperandTypes.TEXT,
        text: 'Model',
        value: {
          operandType: OperandTypes.TEXT,
          value: {
            type: ValueTypes.FIELD,
            field: 'model',
            label: 'model',
            model: 'device',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable.manufacturer = {
        type: OperandTypes.TEXT,
        text: 'Manufacturer',
        value: {
          operandType: OperandTypes.TEXT,
          value: {
            type: ValueTypes.FIELD,
            field: 'manufacturer',
            label: 'manufacturer',
            model: 'device',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable.app_version = {
        type: OperandTypes.TEXT,
        text: 'App version',
        value: {
          operandType: OperandTypes.TEXT,
          value: {
            type: ValueTypes.FIELD,
            field: 'app_version',
            label: 'app_version',
            model: 'device',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['ip_address.ip_address'] = {
        type: OperandTypes.TEXT,
        text: 'IP address',
        value: {
          operandType: OperandTypes.TEXT,
          value: {
            type: ValueTypes.FIELD,
            field: 'ip_address.ip_address',
            label: 'ip_address.ip_address',
            model: 'device',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['phone_number.number'] = {
        type: OperandTypes.TEXT,
        text: 'Phone number / Number',
        value: {
          operandType: OperandTypes.TEXT,
          value: {
            type: ValueTypes.FIELD,
            field: 'phone_number.number',
            label: 'phone_number.number',
            model: 'device',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['email_address.email_address'] = {
        type: OperandTypes.TEXT,
        text: 'Email address',
        value: {
          operandType: OperandTypes.TEXT,
          value: {
            type: ValueTypes.FIELD,
            field: 'email_address.email_address',
            label: 'email_address.email_address',
            model: 'device',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['geolocation.country'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / Country',
        value: {
          operandType: OperandTypes.TEXT,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.country',
            label: 'geolocation.country',
            model: 'device',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['geolocation.city'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / City',
        value: {
          operandType: OperandTypes.TEXT,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.city',
            label: 'geolocation.city',
            model: 'device',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['geolocation.postal_code'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / Postal code',
        value: {
          operandType: OperandTypes.TEXT,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.postal_code',
            label: 'geolocation.postal_code',
            model: 'device',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['geolocation.longitude'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / Longitude',
        value: {
          operandType: OperandTypes.TEXT,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.longitude',
            label: 'geolocation.longitude',
            model: 'device',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
      oldOrNewSymbolTable['geolocation.latitude'] = {
        type: OperandTypes.TEXT,
        text: 'Geolocation / Latitude',
        value: {
          operandType: OperandTypes.TEXT,
          value: {
            type: ValueTypes.FIELD,
            field: 'geolocation.latitude',
            label: 'geolocation.latitude',
            model: 'device',
            datatype: 'string',
            cardinality: 'many',
          },
        },
      };
    }
  }
  // TODO sc-112728: once backfill is complete, only include hardcoded geolocation/focsec
  // constants when the rule is read-only. For editable rules, the data settings should
  // be the source of truth instead. Example:
  // const shouldIncludeHardcodedGeoFocsec = isReadOnly;
  // Then gate the TXN_EVENT_COMMON_*_SYMBOLS and ACTION_EVENT_*_SYMBOLS blocks below
  // with: if (shouldIncludeHardcodedGeoFocsec) { ... }
  if (baseModel === 'TXN_EVENT') {
    if (builderType === 'advanced') {
      if (isRiskRatingsEnabled || hasActiveRiskRatingModel) {
        SENDER_RECEIVER.forEach((direction) => {
          addLatestRiskFieldsToEventForAdvancedBuilder(
            oldOrNewSymbolTable,
            {
              key: `${direction}_entity`,
              label: `Entity / ${toSentenceCase(direction)} entity`,
            },
            Unit21DataClassifier.EVENT,
          );
        });
      }
      for (const [key, value] of Object.entries(
        TXN_EVENT_COMMON_ADVANCED_SYMBOLS,
      ).filter(([fieldKey]) =>
        filterFocsecFieldsIfDisabled(shouldUseFocsecFields, fieldKey),
      )) {
        oldOrNewSymbolTable[key] = value;
      }
      for (const [key, value] of Object.entries(
        TXN_EVENT_SENDER_RECEIVER_ADVANCED_SYMBOLS,
      )) {
        oldOrNewSymbolTable[key] = value;
      }
      SENDER_RECEIVER.forEach((direction) => {
        addNestedGeolocationFieldsForAdvancedBuilder(oldOrNewSymbolTable, {
          key: `${direction}_entity`,
          label: `Entity / ${toSentenceCase(direction)} entity`,
        });
        addNestedGeolocationFieldsForAdvancedBuilder(oldOrNewSymbolTable, {
          key: `${direction}_instrument`,
          label: `Instrument / ${toSentenceCase(direction)} instrument`,
        });
      });
      if (shouldUseTrmFields) {
        SENDER_RECEIVER.forEach((direction) => {
          Object.assign(
            oldOrNewSymbolTable,
            buildTrmFieldsForAdvancedBuilder(
              {
                key: `${direction}_instrument`,
                label: `Instrument / ${toSentenceCase(direction)} instrument`,
              },
              'txn_event',
              shouldUseTrmEntityFields,
            ),
          );
        });
      }
    } else {
      for (const [key, value] of Object.entries(
        TXN_EVENT_SENDER_RECEIVER_ENTITY_DROPDOWN_SYMBOLS,
      ).filter(([fieldKey]) =>
        filterFocsecFieldsIfDisabled(shouldUseFocsecFields, fieldKey),
      )) {
        oldOrNewSymbolTable[key] = value;
      }
      for (const [key, value] of Object.entries(
        TXN_EVENT_COMMON_DROPDOWN_SYMBOLS,
      ).filter(([fieldKey]) =>
        filterFocsecFieldsIfDisabled(shouldUseFocsecFields, fieldKey),
      )) {
        oldOrNewSymbolTable[key] = value;
      }
      SENDER_RECEIVER.forEach((direction) => {
        addNestedGeolocationFieldsForDropdownBuilder(oldOrNewSymbolTable, {
          key: `${direction}_entity`,
          label: `Entity / ${toSentenceCase(direction)} entity`,
        });
      });
      SENDER_RECEIVER.forEach((direction) => {
        addNestedGeolocationFieldsForDropdownBuilder(oldOrNewSymbolTable, {
          key: `${direction}_instrument`,
          label: `Instrument / ${toSentenceCase(direction)} instrument`,
        });
      });
      if (shouldUseTrmFields) {
        SENDER_RECEIVER.forEach((direction) => {
          Object.assign(
            oldOrNewSymbolTable,
            buildTrmFieldsForDropdownBuilder(
              Unit21DataClassifier.EVENT,
              {
                key: `${direction}_instrument`,
                label: `Instrument / ${toSentenceCase(direction)} instrument`,
              },
              'txn_event',
              shouldUseTrmEntityFields,
            ),
          );
        });
      }
      SENDER_RECEIVER.forEach((direction) => {
        addLatestRiskFieldsToEventForDropdownBuilder(
          oldOrNewSymbolTable,
          {
            key: `${direction}_entity`,
            label: `Entity / ${toSentenceCase(direction)} entity`,
          },
          Unit21DataClassifier.EVENT,
          isRiskRatingsEnabled,
          hasActiveRiskRatingModel,
        );
      });
    }
  }
  if (baseModel === 'ACTION_EVENT') {
    if (builderType === 'advanced') {
      if (isRiskRatingsEnabled || hasActiveRiskRatingModel) {
        addLatestRiskFieldsToEventForAdvancedBuilder(
          oldOrNewSymbolTable,
          {
            key: 'entity',
            label: 'Entity',
          },
          Unit21DataClassifier.ACTION_EVENT,
        );
      }
      for (const [key, value] of Object.entries(
        ACTION_EVENT_ADVANCED_SYMBOLS,
      ).filter(([fieldKey]) =>
        filterFocsecFieldsIfDisabled(shouldUseFocsecFields, fieldKey),
      )) {
        oldOrNewSymbolTable[key] = value;
      }
      if (shouldUseTrmFields) {
        Object.assign(
          oldOrNewSymbolTable,
          buildTrmFieldsForAdvancedBuilder(
            {
              key: 'txn_instrument',
              label: 'Instrument',
            },
            'action_event',
            shouldUseTrmEntityFields,
          ),
        );
      }
    } else {
      for (const [key, value] of Object.entries(
        ACTION_EVENT_DROPDOWN_SYMBOLS,
      ).filter(([fieldKey]) =>
        filterFocsecFieldsIfDisabled(shouldUseFocsecFields, fieldKey),
      )) {
        oldOrNewSymbolTable[key] = value;
      }
      if (shouldUseTrmFields) {
        Object.assign(
          oldOrNewSymbolTable,
          buildTrmFieldsForDropdownBuilder(
            Unit21DataClassifier.ACTION_EVENT,
            {
              key: 'txn_instrument',
              label: 'Instrument',
            },
            'action_event',
            shouldUseTrmEntityFields,
          ),
        );
      }
      addLatestRiskFieldsToEventForDropdownBuilder(
        oldOrNewSymbolTable,
        {
          key: 'entity',
          label: 'Entity',
        },
        Unit21DataClassifier.ACTION_EVENT,
        isRiskRatingsEnabled,
        hasActiveRiskRatingModel,
      );
    }
  }
  return oldOrNewSymbolTable;
};

const convertDataSettingEnumsToDropdownValues = (
  enums: string[],
  type: DataSettingFieldType,
) => {
  let customDataDropdownValues: U21SelectOptionProps[] = [];
  if (type === 'ENUM' && enums) {
    customDataDropdownValues = enums.map((enumValue) => {
      return {
        text: enumValue,
        value: enumValue,
      };
    });
  }
  return customDataDropdownValues;
};

export const generateCustomDataSymbolsForPerspectives = (
  builderType: 'dropdown' | 'advanced',
  model: DmbQueryBuilderOptionCode,
  perspective: CustomDataSettingsConfigResponse,
  customDataField: CustomDataSettingsConfigResponse,
): { label: string; symbolValue: OperandSymbol | SymbolFieldValue } => {
  const { key_path: keyPath } = perspective;
  const {
    enum_set: enums,
    data_type: customDataFieldType,
    key_path: customDataKeyPath,
  } = customDataField;
  const dropdownValues = convertDataSettingEnumsToDropdownValues(
    enums,
    customDataFieldType,
  );

  // perspective e.g Transaction / Perspective / ...
  const reference = keyPath[keyPath.length - 1];
  const column = `${customDataKeyPath.join(' / ')}`;
  const label = `Transaction / ${reference} / Custom Data / ${column}`;

  const fieldColumn: string = `custom_data.${customDataKeyPath.join('.')}`;
  const field = `${perspective.perspective}.${fieldColumn}`;
  let symbolValue: OperandSymbol | SymbolFieldValue;
  const type = DATATYPE_MAP[customDataFieldType] || 'text';
  if (builderType === 'dropdown') {
    symbolValue = {
      type,
      text: label,
      value: {
        operandType: dropdownValues.length ? 'multiselect' : type,
        value: {
          type: ValueTypes.FIELD,
          datatype: type === 'text' ? 'string' : type,
          model,
          field,
          label,
        },
        dropdownValues,
      },
    };
  } else {
    symbolValue = {
      type: ValueTypes.FIELD,
      datatype: type === 'text' ? 'string' : type,
      model,
      field,
      label, // Use the prefixed text for the advanced builder as well
    };
  }
  return { label, symbolValue };
};

export const generateNativeReferenceSymbolsForPerspectives = (
  builderType: 'dropdown' | 'advanced',
  model: DmbQueryBuilderOptionCode,
  nativeDataSetting: NativeDataSettingsConfigResponse,
  dataSetting: CustomDataSettingsConfigResponse,
): { field: string; symbolValue: OperandSymbol | SymbolFieldValue } => {
  const { key_path: keyPath } = dataSetting;
  const {
    native_key: labelColumn,
    enum_set: enums,
    data_type: nativeDataType,
    unit21_data_classifier: associationTable,
  } = nativeDataSetting;
  const dropdownValues = convertDataSettingEnumsToDropdownValues(
    enums,
    nativeDataType,
  );

  const reference = keyPath[keyPath.length - 1];
  const label = `Transaction / ${reference} / ${associationTable.toLowerCase()} / ${labelColumn}`;

  const field = `${dataSetting.perspective}.${associationTable.toLowerCase()}.${labelColumn.toLowerCase()}`;
  let symbolValue: OperandSymbol | SymbolFieldValue;
  const type = DATATYPE_MAP[nativeDataType] || 'text';

  if (builderType === 'dropdown') {
    symbolValue = {
      type,
      text: label,
      value: {
        operandType: dropdownValues.length ? 'multiselect' : type,
        value: {
          type: ValueTypes.FIELD,
          datatype: type === 'text' ? 'string' : type,
          model,
          field,
          label,
        },
        dropdownValues,
      },
    };
  } else {
    symbolValue = {
      type: ValueTypes.FIELD,
      datatype: type === 'text' ? 'string' : type,
      model,
      field,
      label, // Use the prefixed text for the advanced builder as well
    };
  }

  return { field, symbolValue };
};

export const generateNativeSymbolsForPerspectives = (
  builderType: 'dropdown' | 'advanced',
  model: DmbQueryBuilderOptionCode,
  nativeDataSetting: NativeDataSettingsConfigResponse,
  dataSetting: CustomDataSettingsConfigResponse,
): { field: string; symbolValue: OperandSymbol | SymbolFieldValue } => {
  const { key_path: keyPath } = dataSetting;
  const {
    native_field: perspectiveField,
    enum_set: enums,
    data_type: nativeDataType,
  } = nativeDataSetting;
  const dropdownValues = convertDataSettingEnumsToDropdownValues(
    enums,
    nativeDataType,
  );

  let column = perspectiveField;
  let fieldColumn:
    | NativeEntityDataSettingsConfigResponse
    | NativeInstrumentDataSettingsConfigResponse
    | string = nativeDataSetting.native_key;
  if (fieldColumn === 'entity_type') {
    column = 'type';
    fieldColumn = 'type';
  } else if (fieldColumn === 'entity_subtype') {
    column = 'internal_entity_type';
    fieldColumn = 'internal_entity_type';
  }

  const reference = keyPath[keyPath.length - 1];
  const label = `Transaction / ${reference} / ${column}`;

  const field = `${dataSetting.perspective}.${fieldColumn}`;
  let symbolValue: OperandSymbol | SymbolFieldValue;
  const type = DATATYPE_MAP[nativeDataType] || 'text';
  if (builderType === 'dropdown') {
    symbolValue = {
      type,
      text: label,
      value: {
        operandType: dropdownValues.length ? 'multiselect' : type,
        value: {
          type: ValueTypes.FIELD,
          datatype: type === 'text' ? 'string' : type,
          model,
          field,
          label,
        },
        dropdownValues,
      },
    };
  } else {
    symbolValue = {
      type: ValueTypes.FIELD,
      datatype: type === 'text' ? 'string' : type,
      model,
      field,
      label, // Use the prefixed text for the advanced builder as well
    };
  }

  return { field, symbolValue };
};

const generateCustomDataSymbols = (
  symbolTable: SymbolTable,
  builderType: 'dropdown' | 'advanced',
  baseModel: DmbQueryBuilderOption,
  model: DmbQueryBuilderOptionCode,
  { key: prefixKey, label: prefixLabel }: Prefix,
  dropdownValues: U21SelectOptionProps[],
  dataSetting: CustomDataSettingsConfigResponse,
): SymbolTable => {
  const newSymbolTable = symbolTable;
  const {
    data_type: dataType,
    key_path: keyPath,
    user_facing_label: userFacingLabel,
    unit21_data_classifier: unit21DataClassifier,
  } = dataSetting;
  const formattedModelPrefix: string = prefixKey;
  const shouldAddModelPrefix: boolean = !(
    (prefixKey === 'txn_instrument' && baseModel === 'INSTRUMENT') ||
    prefixKey === baseModel.toLowerCase()
  );
  const shouldAddTxnDirectionPrefix: boolean =
    /(sender|receiver|perspective)_(entity|instrument)/gi.test(prefixKey);
  const shouldAddPrefix = shouldAddModelPrefix || shouldAddTxnDirectionPrefix;
  const dataPathDotNotation = `custom_data.${keyPath.join('.')}`;
  const formattedField = `${
    shouldAddModelPrefix ? `${formattedModelPrefix}.` : ''
  }${dataPathDotNotation}`;

  let text = keyPathToLabel(dataSetting, false, ' / ');
  if (!userFacingLabel) {
    text = `Custom Data / ${text}`;
  }
  if (shouldAddTxnDirectionPrefix) {
    text = `${prefixLabel} / ${text}`;
  }

  // Add classifier-based prefix
  if (unit21DataClassifier === Unit21DataClassifier.ACTION_EVENT) {
    text = `Action / ${text}`;
  } else if (unit21DataClassifier === Unit21DataClassifier.EVENT) {
    text = `Transaction / ${text}`;
  } else if (unit21DataClassifier === Unit21DataClassifier.ENTITY) {
    text = `Entity / ${text}`;
  } else if (unit21DataClassifier === Unit21DataClassifier.INSTRUMENT) {
    text = `Instrument / ${text}`;
  }

  const field = `${shouldAddPrefix ? `${prefixKey}.` : ''}${dataPathDotNotation}`;
  let symbolValue: OperandSymbol | SymbolFieldValue;
  const customDataType = DATATYPE_MAP[dataType] || 'text';
  if (builderType === 'dropdown') {
    symbolValue = {
      type: customDataType,
      text,
      value: {
        operandType: dropdownValues.length ? 'multiselect' : customDataType,
        value: {
          type: ValueTypes.FIELD,
          datatype: customDataType === 'text' ? 'string' : customDataType,
          model,
          field,
          label: formattedField,
        },
        dropdownValues,
      },
    };
  } else {
    symbolValue = {
      type: ValueTypes.FIELD,
      datatype: customDataType === 'text' ? 'string' : customDataType,
      model,
      field,
      label: text, // Use the prefixed text for the advanced builder as well
    };
  }

  newSymbolTable[formattedField] = symbolValue;
  return newSymbolTable;
};

export const isNotEventListField = (cd: CustomDataSettingsConfigResponse) =>
  !(
    [Unit21DataClassifier.EVENT, Unit21DataClassifier.ACTION_EVENT].includes(
      cd.unit21_data_classifier,
    ) && cd.data_type === DataSettingFieldType.LIST
  );

export const mergeSymbolTableWithCustomData = (
  symbolTable: SymbolTable,
  nativeData: NativeDataSettingsConfigResponse[],
  customData: CustomDataSettingsConfigResponse[],
  baseModel: DmbQueryBuilderOption,
  builderType: 'dropdown' | 'advanced',
  isRuleEventSchemaV21Enabled: boolean,
  shouldUseTrmFields: boolean = false,
  shouldUseTrmEntityFields: boolean = false,
): SymbolTable => {
  let newSymbolTable: SymbolTable | undefined;
  customData
    .filter((dataSetting) => {
      return (
        SUPPORTED_DATA_CLASSIFIER.has(dataSetting.unit21_data_classifier) &&
        !PRIMITIVE_TYPES_NOT_SUPPORTED_IN_DM.has(dataSetting.data_type)
      );
    })
    .forEach((dataSetting) => {
      const {
        data_type: dataType,
        enum_set: enumSet,
        unit21_data_classifier: unit21DataClassifier,
      } = dataSetting;
      const lowerCasedClassifier: string = unit21DataClassifier.toLowerCase();

      let customDataDropdownValues: U21SelectOptionProps[] = [];
      if (dataType === 'ENUM' && enumSet) {
        customDataDropdownValues = enumSet.map((enumValue) => {
          return {
            text: enumValue,
            value: enumValue,
          };
        });
      }

      if (baseModel === 'TXN_EVENT') {
        if (
          unit21DataClassifier === Unit21DataClassifier.ENTITY ||
          unit21DataClassifier === Unit21DataClassifier.INSTRUMENT
        ) {
          SENDER_RECEIVER.forEach((direction) => {
            newSymbolTable = generateCustomDataSymbols(
              symbolTable,
              builderType,
              baseModel,
              DM_OBJECT_ENUM_TO_CODE_MAP[baseModel],
              {
                key: `${direction}_${lowerCasedClassifier}`,
                label: `${toSentenceCase(direction)} ${lowerCasedClassifier}`,
              },
              customDataDropdownValues,
              dataSetting,
            );
          });
        } else if (
          isRuleEventSchemaV21Enabled &&
          unit21DataClassifier === Unit21DataClassifier.EVENT &&
          (dataSetting.data_type === DataSettingFieldType.ENTITY_REFERENCE ||
            dataSetting.data_type === DataSettingFieldType.INSTRUMENT_REFERENCE)
        ) {
          // data settings are really wonky
          // entity_id_1, id_2, etc... is seen as a custom data setting on the txn_event model
          // we need to map it as a native data setting to reference the entity correctly
          // and THEN we need to map the custom data settings to that native data model

          // e.g txn_event.entity_id_1.native_field
          // custom_data_field -> native model -> native_field for native model
          nativeData
            .filter(
              (nativeDataSetting) =>
                dataSetting.perspective
                  ?.toUpperCase()
                  .includes(
                    nativeDataSetting.unit21_data_classifier.toUpperCase(),
                  ) &&
                nativeDataSetting.data_type !== DataSettingFieldType.LIST,
            )
            .forEach((nativeDataSetting) => {
              const { field, symbolValue } =
                generateNativeSymbolsForPerspectives(
                  builderType,
                  DM_OBJECT_ENUM_TO_CODE_MAP[baseModel],
                  nativeDataSetting,
                  dataSetting,
                );
              if (newSymbolTable !== undefined) {
                newSymbolTable[field] = symbolValue;
              }
            });

          nativeData
            .filter(
              (nativeDataSetting) =>
                nativeDataSetting.unit21_data_classifier === 'ADDRESS' &&
                nativeDataSetting.data_type !== DataSettingFieldType.LIST,
            )
            .forEach((nativeDataSetting) => {
              const { field, symbolValue } =
                generateNativeReferenceSymbolsForPerspectives(
                  builderType,
                  DM_OBJECT_ENUM_TO_CODE_MAP[baseModel],
                  nativeDataSetting,
                  dataSetting,
                );
              if (newSymbolTable !== undefined) {
                newSymbolTable[field] = symbolValue;
              }
            });

          // txn_event.entity_id_1.custom_data.my_field
          // custom_data_field -> native model -> custom_data_field for native model
          customData
            .filter((setting) => {
              return (
                (dataSetting.data_type ===
                  DataSettingFieldType.ENTITY_REFERENCE ||
                  dataSetting.data_type ===
                    DataSettingFieldType.INSTRUMENT_REFERENCE) &&
                dataSetting.data_type.startsWith(
                  setting.unit21_data_classifier,
                ) && // hack
                setting.data_type !== DataSettingFieldType.LIST
              );
            })
            .forEach((setting) => {
              const symbols = generateCustomDataSymbolsForPerspectives(
                builderType,
                DM_OBJECT_ENUM_TO_CODE_MAP[baseModel],
                dataSetting,
                setting,
              );
              if (newSymbolTable !== undefined) {
                newSymbolTable[symbols.label] = symbols.symbolValue;
              }
            });

          if (
            shouldUseTrmFields &&
            dataSetting.data_type ===
              DataSettingFieldType.INSTRUMENT_REFERENCE &&
            dataSetting.perspective
          ) {
            if (newSymbolTable === undefined) {
              newSymbolTable = { ...symbolTable };
            }
            const reference =
              dataSetting.key_path[dataSetting.key_path.length - 1];
            const trmFields =
              builderType === 'advanced'
                ? buildTrmFieldsForAdvancedBuilder(
                    {
                      key: dataSetting.perspective,
                      label: `Transaction / ${reference}`,
                    },
                    'txn_event',
                    shouldUseTrmEntityFields,
                  )
                : buildTrmFieldsForDropdownBuilder(
                    Unit21DataClassifier.EVENT,
                    {
                      key: dataSetting.perspective,
                      label: `Transaction / ${reference}`,
                    },
                    'txn_event',
                    shouldUseTrmEntityFields,
                  );
            Object.assign(newSymbolTable, trmFields);
          }
        } else if (
          unit21DataClassifier === Unit21DataClassifier.EVENT ||
          unit21DataClassifier === Unit21DataClassifier.ADDRESS
        ) {
          newSymbolTable = generateCustomDataSymbols(
            symbolTable,
            builderType,
            baseModel,
            CLASSIFIER_SECTION_MAP[unit21DataClassifier],
            {
              key: CLASSIFIER_SECTION_MAP[unit21DataClassifier],
              label: CLASSIFIER_SECTION_MAP_FRIENDLY_NAME[unit21DataClassifier],
            },
            customDataDropdownValues,
            dataSetting,
          );
        }
      } else if (baseModel === Unit21DataClassifier.ACTION_EVENT) {
        if (
          unit21DataClassifier === Unit21DataClassifier.ENTITY ||
          unit21DataClassifier === Unit21DataClassifier.INSTRUMENT
        ) {
          newSymbolTable = generateCustomDataSymbols(
            symbolTable,
            builderType,
            baseModel,
            DM_OBJECT_ENUM_TO_CODE_MAP[baseModel],
            {
              key: CLASSIFIER_SECTION_MAP[unit21DataClassifier],
              label: CLASSIFIER_SECTION_MAP_FRIENDLY_NAME[unit21DataClassifier],
            },
            customDataDropdownValues,
            dataSetting,
          );
        } else if (
          unit21DataClassifier === Unit21DataClassifier.ACTION_EVENT ||
          unit21DataClassifier === Unit21DataClassifier.ADDRESS
        ) {
          newSymbolTable = generateCustomDataSymbols(
            symbolTable,
            builderType,
            baseModel,
            CLASSIFIER_SECTION_MAP[unit21DataClassifier],
            {
              key: CLASSIFIER_SECTION_MAP[unit21DataClassifier],
              label: CLASSIFIER_SECTION_MAP_FRIENDLY_NAME[unit21DataClassifier],
            },
            customDataDropdownValues,
            dataSetting,
          );
        }
      } else if (
        ((baseModel === Unit21DataClassifier.ENTITY ||
          // we need entity symbols for consortium rules
          baseModel === 'CONSORTIUM') &&
          (unit21DataClassifier === Unit21DataClassifier.ENTITY ||
            unit21DataClassifier === Unit21DataClassifier.ADDRESS)) ||
        (baseModel === Unit21DataClassifier.INSTRUMENT &&
          (unit21DataClassifier === Unit21DataClassifier.INSTRUMENT ||
            unit21DataClassifier === Unit21DataClassifier.ADDRESS))
      ) {
        newSymbolTable = generateCustomDataSymbols(
          symbolTable,
          builderType,
          baseModel,
          CLASSIFIER_SECTION_MAP[unit21DataClassifier],
          {
            key: CLASSIFIER_SECTION_MAP[unit21DataClassifier],
            label: CLASSIFIER_SECTION_MAP_FRIENDLY_NAME[unit21DataClassifier],
          },
          customDataDropdownValues,
          dataSetting,
        );
      }
    });
  return newSymbolTable || symbolTable;
};

export const mergeSymbolTableWithDataSettings = (
  nativeData: NativeDataSettingsConfigResponse[],
  customData: CustomDataSettingsConfigResponse[],
  baseModel: DmbQueryBuilderOption,
  builderType: 'dropdown' | 'advanced',
  isRuleEventSchemaV21Enabled: boolean = false,
  shouldUseFocsecFields: boolean = false,
  isRiskRatingsEnabled: boolean = false,
  hasActiveRiskRatingModel: boolean = false,
  shouldUseTrmFields: boolean = false,
  shouldUseTrmEntityFields: boolean = false,
): SymbolTable => {
  let symbolTable: SymbolTable = {};
  if (nativeData)
    symbolTable = mergeSymbolTableWithNativeData(
      symbolTable,
      nativeData,
      baseModel,
      builderType,
      shouldUseFocsecFields,
      isRiskRatingsEnabled,
      hasActiveRiskRatingModel,
      shouldUseTrmFields,
      shouldUseTrmEntityFields,
    );
  if (customData)
    symbolTable = mergeSymbolTableWithCustomData(
      symbolTable,
      nativeData,
      customData,
      baseModel,
      builderType,
      isRuleEventSchemaV21Enabled,
      shouldUseTrmFields,
      shouldUseTrmEntityFields,
    );

  return symbolTable;
};

const deriveModel = (classifier: string) => {
  if (classifier === Unit21DataClassifier.INSTRUMENT) {
    return 'txn_instrument';
  }
  if (classifier === Unit21DataClassifier.EVENT) {
    return 'txn_event';
  }

  return classifier.toLowerCase();
};

export const transformCustomDataSettingsValueToFieldValue = (
  customData: CustomDataSettingsConfigResponse,
): FieldValue => {
  return {
    datatype: DATATYPE_MAP[customData.data_type],
    field: `custom_data.${customData.key_path[0]}`,
    type: ValueTypes.FIELD,
    model: deriveModel(customData.unit21_data_classifier),
  };
};

// Maps native_key to the backend-expected field name for Rules.
// Classifier-agnostic overrides go here; classifier-specific ones are looked up
// from NATIVE_DATA_SETTINGS_NATIVE_KEY_MISMATCH_MAP at runtime.
const NATIVE_KEY_TO_FIELD_MAP: Record<string, string> = {
  event_id: 'id',
  // NOTE: "fee" is the DIM label but the backend column is "internal_fee"
  fee: 'internal_fee',
};

export const transformNativeDataSettingsValueToFieldValue = (
  nativeData: NativeDataSettingsConfigResponse,
): FieldValue => {
  const classifierOverrides =
    NATIVE_DATA_SETTINGS_NATIVE_KEY_MISMATCH_MAP[
      nativeData.unit21_data_classifier
    ];
  const field =
    NATIVE_KEY_TO_FIELD_MAP[nativeData.native_key] ??
    classifierOverrides?.[nativeData.native_key] ??
    nativeData.native_key;
  return {
    datatype: DATATYPE_MAP[nativeData.data_type],
    field,
    type: ValueTypes.FIELD,
    model: deriveModel(nativeData.unit21_data_classifier),
  };
};

export const transformCustomDataToFieldValue = (
  dataSetting:
    | NativeDataSettingsConfigResponse
    | CustomDataSettingsConfigResponse,
): FieldValue => {
  let field = '';
  if (dataSetting.key_type === DataSettingsKeyType.NATIVE) {
    field = dataSetting.native_key;
  } else if (dataSetting.perspective === undefined) {
    field = keyPathToLabel(dataSetting);
  } else {
    field = dataSetting.perspective;
  }

  return {
    datatype: DATATYPE_MAP[dataSetting.data_type],
    field,
    type: ValueTypes.FIELD,
    model: deriveModel(dataSetting.unit21_data_classifier),
  };
};

const DATATYPE_MAP: Record<DataSettingFieldType, OperandTypes> = {
  ANY: OperandTypes.TEXT,
  TEXT: OperandTypes.TEXT,
  NUMBER: OperandTypes.NUMBER,
  BOOLEAN: OperandTypes.BOOLEAN,
  DATE: OperandTypes.DATE,
  DATE_TIME: OperandTypes.DATETIME,
  ENUM: OperandTypes.ENUM,
  LIST: OperandTypes.LIST,
  ENTITY_REFERENCE: OperandTypes.TEXT,
  INSTRUMENT_REFERENCE: OperandTypes.TEXT,
  JSON: OperandTypes.TEXT, // not sure what to do here
};

export const determineOperator = (
  condition: CompositeQueryTreeNode,
): DmOperator => {
  // NOTE: condition's operator should never be BETWEEN
  if (!(condition.operator in ConditionalOperators)) return condition.operator;

  const leftOperand = condition.operands[0];
  const rightOperand = condition.operands[1];
  let operatorForBE = condition.operator;

  if (leftOperand) {
    const leftOperandIsFieldType = leftOperand.type === ValueTypes.FIELD;
    const rightOperandIsFieldType =
      rightOperand && rightOperand.type === ValueTypes.FIELD;
    const isNestedStandardField =
      leftOperandIsFieldType &&
      !leftOperand.field.includes('custom_data') &&
      leftOperand.field.includes('.');
    const isNotTxnEvent =
      leftOperandIsFieldType && leftOperand.model !== 'txn_event';
    const isSenderOrReceiver =
      leftOperandIsFieldType &&
      ['sender', 'receiver'].some((value) => leftOperand.field.includes(value));
    const isNotUserOrBusiness =
      leftOperandIsFieldType &&
      !['user', 'business'].some((value) => leftOperand.field.includes(value));
    const isManyCardinality =
      leftOperandIsFieldType &&
      isNotUserOrBusiness &&
      (isSenderOrReceiver || isNotTxnEvent) &&
      isNestedStandardField;

    if (leftOperandIsFieldType) {
      switch (condition.operator) {
        case ConditionalOperators.EQ: // one to one
        case ConditionalOperators.ANY_EQ:
          if (rightOperandIsFieldType) {
            if (
              leftOperand.cardinality === 'many' &&
              rightOperand.cardinality === 'many'
            )
              operatorForBE = ConditionalOperators.ANY_IN;
            else if (
              leftOperand.cardinality === 'one' &&
              rightOperand.cardinality === 'one'
            )
              operatorForBE = ConditionalOperators.EQ;
            else operatorForBE = ConditionalOperators.ANY_EQ;
          } else if (leftOperand.cardinality === 'many') {
            // rightOperand is not a single FieldValue;
            if (rightOperandIsFieldType && isArray(rightOperand))
              operatorForBE = ConditionalOperators.ANY_IN;
            else operatorForBE = ConditionalOperators.ANY_EQ;
          } else if (
            rightOperand &&
            rightOperand.type === ValueTypes.CONSTANT
          ) {
            operatorForBE = isArray(rightOperand)
              ? ConditionalOperators.IN
              : ConditionalOperators.EQ;
          }
          break;
        case ConditionalOperators.NEQ:
        case ConditionalOperators.NONE_EQ:
          if (rightOperandIsFieldType) {
            operatorForBE =
              leftOperand.datatype === rightOperand.datatype
                ? ConditionalOperators.NEQ
                : ConditionalOperators.NONE_IN;
          } else if (
            rightOperand &&
            rightOperand.type === ValueTypes.CONSTANT &&
            isArray(rightOperand.value)
          ) {
            operatorForBE = ConditionalOperators.NONE_IN;
          } else if (
            leftOperand.field.includes('custom_data') &&
            leftOperand.field.includes('.')
          ) {
            operatorForBE = ConditionalOperators.NONE_EQ;
          } else operatorForBE = ConditionalOperators.NEQ;
          break;
        case ConditionalOperators.LIKE:
        case ConditionalOperators.ANY_LIKE:
          operatorForBE = isManyCardinality
            ? ConditionalOperators.ANY_LIKE
            : ConditionalOperators.LIKE;
          break;
        case ConditionalOperators.NOT_LIKE:
        case ConditionalOperators.NONE_LIKE:
          operatorForBE = isManyCardinality
            ? ConditionalOperators.NONE_LIKE
            : ConditionalOperators.NOT_LIKE;
          break;
        case ConditionalOperators.IN:
        case ConditionalOperators.ANY_IN:
          operatorForBE = isManyCardinality
            ? ConditionalOperators.ANY_IN
            : ConditionalOperators.IN;
          break;
        case ConditionalOperators.NOT_IN:
        case ConditionalOperators.NONE_IN:
          operatorForBE = isManyCardinality
            ? ConditionalOperators.NONE_IN
            : ConditionalOperators.NOT_IN;
          break;
        case ConditionalOperators.IS_EMPTY:
          operatorForBE = isManyCardinality
            ? ConditionalOperators.ANY_IS_EMPTY
            : ConditionalOperators.IS_EMPTY;
          break;
        case ConditionalOperators.IS_NOT_EMPTY:
          operatorForBE = isManyCardinality
            ? ConditionalOperators.NONE_IS_EMPTY
            : ConditionalOperators.IS_NOT_EMPTY;
          break;
        default:
      }
    }
  }
  return operatorForBE as ConditionalOperators;
};

export const EMPTY_OPERATORS = () =>
  new Set<string>([
    ConditionalOperators.IS_EMPTY,
    ConditionalOperators.IS_NOT_EMPTY,
    ConditionalOperators.ANY_IS_EMPTY,
    ConditionalOperators.NONE_IS_EMPTY,
  ]);

export const transformConditionOperatorsForBE = (
  condition: CompositeQueryTreeNode,
): CompositeQueryTreeNode => {
  if (!condition.operands.length) return condition;
  const conditionCopy = cloneDeep(condition);
  condition.operands.forEach((operand, index) => {
    if (
      operand?.type === CompositeTypes.RULE_CONDITION_COMPOSITE &&
      operand.operator !== ConditionalOperators.BETWEEN &&
      conditionCopy.operands[index] !== null
    ) {
      const operandCopy = conditionCopy.operands[index];
      const operatorForBE = determineOperator(operand);
      if (
        operandCopy &&
        operandCopy.type === CompositeTypes.RULE_CONDITION_COMPOSITE
      )
        operandCopy.operator = operatorForBE;
      transformConditionOperatorsForBE(operand);
    }
  });
  return conditionCopy;
};
// NOTE: end Dropdown builder helper functions

export const isLegacySequenceRule = (scenarioName: string) => {
  return LEGACY_SEQUENCE_RULES.some((currName) => scenarioName === currName);
};
export const isAchRiskScoreEnabledRule = (scenarioName: string) => {
  return ACH_RISK_SCORE_ENABLED_SCENARIO_RULES.some(
    (currName) => scenarioName === currName,
  );
};

export const isConsortiumRule = (scenarioName: string) => {
  return CONSORTIUM_RULES.includes(scenarioName);
};

export const isWatchlistRule = (scenarioName: string) => {
  return scenarioName.toLowerCase() === WATCHLIST_RULE_NAME;
};

export const isCheckFraudRuleDSN = (scenarioName: string): boolean => {
  return scenarioName === SCENARIO_DUPLICATE_SERIAL_NUMBER_NAME;
};

export const isCheckFraudRuleMSN = (scenarioName: string): boolean => {
  return scenarioName === SCENARIO_MISSING_SERIAL_NUMBER_NAME;
};

export const isSequenceRollingWindowRule = (scenarioName: string): boolean => {
  return (
    scenarioName === SCENARIO_SEQUENCE_ROLLING_WINDOW_NAME ||
    scenarioName === SCENARIO_SEQUENCE_ROLLING_WINDOW_WITH_COUNT_NAME ||
    scenarioName === SCENARIO_SEQUENCE_ROLLING_WINDOW_ROUND_TRIP_NAME
  );
};

export const isSequenceRollingWindowOneStepRule = (
  scenarioName: string,
): boolean => {
  return scenarioName === SCENARIO_SEQUENCE_ROLLING_WINDOW_ONE_STEP_NAME;
};

export const isSequenceRollingWindowWithCount = (
  scenarioName: string,
): boolean => {
  return scenarioName === SCENARIO_SEQUENCE_ROLLING_WINDOW_WITH_COUNT_NAME;
};

export const isSequenceRollingWindowRoundTrip = (
  scenarioName: string,
): boolean => {
  return scenarioName === SCENARIO_SEQUENCE_ROLLING_WINDOW_ROUND_TRIP_NAME;
};

export const isSequenceCumulativeAggregateRule = (
  scenarioName: string,
): boolean => {
  return scenarioName === SCENARIO_SEQUENCE_CUMULATIVE_AGGREGATE_NAME;
};

export const isSequenceCumulativeAggregateUSPSRule = (
  scenarioName: string,
): boolean => {
  return scenarioName === SCENARIO_SEQUENCE_CUMULATIVE_AGGREGATE_USPS_NAME;
};

export const isSequenceDailyAggregateRule = (scenarioName: string): boolean => {
  return scenarioName === SCENARIO_SEQUENCE_DAILY_AGGREGATE_NAME;
};

export const isCheckFraudRule = (scenarioName: string): boolean => {
  return CHECK_FRAUD_RULES_ALL.includes(scenarioName);
};

export const isScenarioBuilderCheckFraudRule = (
  scenarioName: string,
): boolean => {
  return SCENARIO_BUILDER_CHECK_FRAUD_RULES.includes(scenarioName);
};

export const isAchMultipleFailedValidationAttemptsRule = (
  scenarioName: string,
): boolean => {
  return scenarioName === SCENARIO_MULTIPLE_FAILED_VALIDATION_ATTEMPTS_NAME;
};
export const isAchRiskScoreRule = (scenarioName: string): boolean => {
  return scenarioName === SCENARIO_ACH_RISK_SCORE_NAME;
};

export const isAchConsortiumRule = (scenarioName: string): boolean => {
  return scenarioName === SCENARIO_ACH_CONSORTIUM_NAME;
};
export const isAchMismatchedRule = (scenarioName: string): boolean => {
  return scenarioName === SCENARIO_ACH_MISMATCHED_NAME;
};
export const isAchDuplicatedRule = (scenarioName: string): boolean => {
  return scenarioName === SCENARIO_ACH_DUPLICATE_NAME;
};

export const shouldHideExecutionFrequency = (
  scenarioConfig: ScenarioConfig,
) => {
  if (DORMANT_ACTIVITY_NAME === scenarioConfig.name) {
    return true;
  }
  if (scenarioConfig.scenario_type === 'WINDOWED') {
    if ([SCENARIO_CONSORTIUM_NEW_ACCOUNT_NAME].includes(scenarioConfig.name)) {
      return true;
    }
    return false;
  }
  if (scenarioConfig.scenario_type === 'ALL_TIME') {
    if (
      [
        'top_transacting_entities_embedded_filters',
        'top_transacting_entities',
      ].includes(scenarioConfig.name)
    ) {
      return false;
    }
  }

  if (scenarioConfig.scenario_type === 'NON_WINDOWED') {
    return false;
  }

  return true;
};

export const isDmBackEndDateFormat = (string: string): boolean => {
  return /^([1-2]\d{3})-(0[1-9]|1[0-2])-(0[1-9]|1\d|2\d|3[01])\s(?:[01]\d|2[0123]):(?:[012345]\d):(?:[012345]\d)$/.test(
    string,
  );
};

const isCondition = (node: QueryTreeNode): node is CompositeQueryTreeNode => {
  if (!node) {
    return false;
  }
  return (
    node.type === CompositeTypes.COMPOSITE ||
    node.type === CompositeTypes.RULE_CONDITION_COMPOSITE
  );
};

export const countConditions = (
  ruleCondition: QueryTreeNode,
  isPapaNode: boolean,
) => {
  let count = 0;

  if (!isCondition(ruleCondition)) {
    return count;
  }

  if (!isPapaNode) {
    count += 1;
  }

  if (ruleCondition.operands.length > 0) {
    count += ruleCondition.operands.reduce(
      (acc, curr) => acc + countConditions(curr, false),
      0,
    );
  }

  return count;
};

const addNestedGeolocationFieldsForDropdownBuilder = (
  symbolTableToUpdate: SymbolTable,
  prefix: Prefix,
) => {
  /* Mutates passed `symbolTableToUpdate` param, adds more keys to the table. */
  const symbolTable = symbolTableToUpdate;
  const geolocationPath = `${prefix.key}.geolocation`;
  const geolocationPathText = `${prefix.label} / Geolocation`;
  symbolTable[`${geolocationPath}.country`] = {
    type: OperandTypes.TEXT,
    text: `${geolocationPathText} / Country`,
    value: {
      operandType: OperandTypes.TEXT,
      unit21DataClassifier: Unit21DataClassifier.EVENT,
      value: {
        type: ValueTypes.FIELD,
        field: `${geolocationPath}.country`,
        label: `${geolocationPath}.country`,
        model: 'txn_event',
        datatype: 'string',
        cardinality: 'many',
      },
    },
  };
  symbolTable[`${geolocationPath}.city`] = {
    type: OperandTypes.TEXT,
    text: `${geolocationPathText} / City`,
    value: {
      operandType: OperandTypes.TEXT,
      unit21DataClassifier: Unit21DataClassifier.EVENT,
      value: {
        type: ValueTypes.FIELD,
        field: `${geolocationPath}.city`,
        label: `${geolocationPath}.city`,
        model: 'txn_event',
        datatype: 'string',
        cardinality: 'many',
      },
    },
  };
  symbolTable[`${geolocationPath}.postal_code`] = {
    type: OperandTypes.TEXT,
    text: `${geolocationPathText} / Postal code`,
    value: {
      operandType: OperandTypes.TEXT,
      unit21DataClassifier: Unit21DataClassifier.EVENT,
      value: {
        type: ValueTypes.FIELD,
        field: `${geolocationPath}.postal_code`,
        label: `${geolocationPath}.postal_code`,
        model: 'txn_event',
        datatype: 'string',
        cardinality: 'many',
      },
    },
  };
  symbolTable[`${geolocationPath}.longitude`] = {
    type: OperandTypes.TEXT,
    text: `${geolocationPathText} / Longitude`,
    value: {
      operandType: OperandTypes.TEXT,
      unit21DataClassifier: Unit21DataClassifier.EVENT,
      value: {
        type: ValueTypes.FIELD,
        field: `${geolocationPath}.longitude`,
        label: `${geolocationPath}.longitude`,
        model: 'txn_event',
        datatype: 'string',
        cardinality: 'many',
      },
    },
  };
  symbolTable[`${geolocationPath}.latitude`] = {
    type: OperandTypes.TEXT,
    text: `${geolocationPathText} / Latitude`,
    value: {
      operandType: OperandTypes.TEXT,
      unit21DataClassifier: Unit21DataClassifier.EVENT,
      value: {
        type: ValueTypes.FIELD,
        field: `${geolocationPath}.latitude`,
        label: `${geolocationPath}.latitude`,
        model: 'txn_event',
        datatype: 'string',
        cardinality: 'many',
      },
    },
  };
};

export const addLatestRiskFieldsToEventForDropdownBuilder = (
  symbolTableToUpdate: SymbolTable,
  prefix: Prefix | null,
  unit21DataClassifier: Unit21DataClassifier,
  isRiskRatingsEnabled: boolean,
  hasActiveRiskRatingModel: boolean,
) => {
  /* Mutates passed `symbolTableToUpdate` param, adds more keys to the table. */
  const symbolTable = symbolTableToUpdate;

  const model = deriveModel(unit21DataClassifier);

  const riskFields = [
    {
      key: U21_LATEST_RISK_SCORE_KEY,
      dataType: 'number',
      text: 'Unit21 Entity risk score',
      operandType: 'number',
    },
    {
      key: U21_RISK_LEVEL_KEY,
      dataType: 'text',
      text: 'Unit21 Entity risk level',
      operandType: 'multiselect',
      dropdownValues: U21_RISK_LEVELS.map((riskLevel) => ({
        text: riskLevel,
        value: riskLevel.toLowerCase(),
      })),
    },
  ] as const;

  riskFields.forEach((field) => {
    const { key, dataType, text, operandType } = field;
    const fieldPath = prefix ? `${prefix.key}.${key}` : key;
    const fieldText = prefix ? `${prefix.label} / ${text}` : text;
    const symbolValue: any = {
      type: dataType,
      text: fieldText,
      value: {
        operandType,
        unit21DataClassifier,
        value: {
          type: ValueTypes.FIELD,
          field: fieldPath,
          label: fieldPath,
          model,
          datatype: dataType,
        },
      },
      disabled: !isRiskRatingsEnabled || !hasActiveRiskRatingModel,
      tooltip: hasActiveRiskRatingModel ? null : RISK_RATINGS_TOOLTIP,
    };

    if ('dropdownValues' in field && field.dropdownValues !== undefined) {
      symbolValue.value.dropdownValues = field.dropdownValues;
    }

    symbolTable[fieldPath] = symbolValue;
  });
};

export const addLatestRiskFieldsToEventForAdvancedBuilder = (
  symbolTableToUpdate: SymbolTable,
  prefix: Prefix | null,
  unit21DataClassifier: Unit21DataClassifier,
) => {
  /* Mutates passed `symbolTableToUpdate` param, adds more keys to the table. */
  const symbolTable = symbolTableToUpdate;

  const model = deriveModel(unit21DataClassifier);

  const riskFields = [
    {
      key: U21_LATEST_RISK_SCORE_KEY,
      dataType: 'number',
      text: 'Unit21 Entity risk score',
    },
    {
      key: U21_RISK_LEVEL_KEY,
      dataType: 'text',
      text: 'Unit21 Entity risk level',
    },
  ] as const;

  riskFields.forEach(({ key, dataType, text }) => {
    const fieldPath = prefix ? `${prefix.key}.${key}` : key;
    const label = prefix ? `${toSentenceCase(prefix.label)} / ${text}` : text;
    symbolTable[fieldPath] = {
      type: ValueTypes.FIELD,
      field: fieldPath,
      label,
      model,
      datatype: dataType,
    };
  });
};

export const getRtrDecision = (decision: string | undefined): string => {
  return decision && DECISION_MAP[decision]
    ? DECISION_MAP[decision]
    : DECISION_MAP.deny;
};

const addNestedGeolocationFieldsForAdvancedBuilder = (
  symbolTableToUpdate: SymbolTable,
  prefix: Prefix,
) => {
  /* Mutates passed `symbolTableToUpdate` param, adds more keys to the table. */
  const symbolTable = symbolTableToUpdate;
  const geolocationPath = `${prefix.key}.geolocation`;
  const geolocationPathText = `${prefix.label} / Geolocation`;
  symbolTable[`${geolocationPath}.country`] = {
    type: ValueTypes.FIELD,
    field: `${geolocationPath}.country`,
    label: `${geolocationPathText} / Country`,
    model: 'txn_event',
    datatype: 'string',
    cardinality: 'many',
  };
  symbolTable[`${geolocationPath}.city`] = {
    type: ValueTypes.FIELD,
    field: `${geolocationPath}.city`,
    label: `${geolocationPathText} / City`,
    model: 'txn_event',
    datatype: 'string',
    cardinality: 'many',
  };
  symbolTable[`${geolocationPath}.postal_code`] = {
    type: ValueTypes.FIELD,
    field: `${geolocationPath}.postal_code`,
    label: `${geolocationPathText} / Postal code`,
    model: 'txn_event',
    datatype: 'string',
    cardinality: 'many',
  };
  symbolTable[`${geolocationPath}.longitude`] = {
    type: ValueTypes.FIELD,
    field: `${geolocationPath}.longitude`,
    label: `${geolocationPathText} / Longitude`,
    model: 'txn_event',
    datatype: 'string',
    cardinality: 'many',
  };
  symbolTable[`${geolocationPath}.latitude`] = {
    type: ValueTypes.FIELD,
    field: `${geolocationPath}.latitude`,
    label: `${geolocationPathText} / Latitude`,
    model: 'txn_event',
    datatype: 'string',
    cardinality: 'many',
  };
};

const MAX_VALUE_LENGTH = 20;

/**
 * Transforms rule match data into a network graph representation.
 *
 * This function takes rule matches and entity data and converts them into a graph structure
 * that can be visualized, showing relationships between entities and their matching fields.
 *
 * @param {Object} params - The parameters for graph generation
 * @param {RuleGraphMatches[]} params.matches - Array of rule matches containing entity and field data
 * @param {ShortEntityResponse[]} [params.entities] - Optional array of entity data for additional node information
 * @param {number} [params.matchesLimit] - Optional limit on number of matches to process
 * @param {number} [params.entityLimit] - Optional limit on number of entities to process per match
 *
 * @returns {U21NetworkGraphElements | null} A graph structure containing nodes and edges, or null if invalid input
 *
 * The returned graph structure contains:
 * - Nodes representing entities and their linked values (e.g., phone numbers, emails)
 * - Edges showing relationships between entities and their linked values
 * - Node metadata including labels, colors, and IDs
 */
export const graphifyMatchData = ({
  matches,
  entities,
  matchesLimit,
  entityLimit,
  palette,
}: {
  matches: RuleGraphMatches[];
  entities?: ShortEntityResponse[];
  matchesLimit?: number;
  entityLimit?: number;
  palette: U21Palette;
}): U21NetworkGraphElements | null => {
  const elements: U21NetworkGraphElements = { nodes: {}, edges: {} };

  if (!matches || matches.length === 0) {
    return null;
  }

  let curNumMatches = 0;
  const seenNodesAndEdges = new Set();
  for (const matchesInfo of matches) {
    curNumMatches += 1;
    // Break when we hit the limit for performance reasons
    if (matchesLimit && curNumMatches > matchesLimit) {
      break;
    }
    const { entity_external_ids: entityExternalIds } = matchesInfo;
    let { field } = matchesInfo;
    if (!entityExternalIds || !field) {
      return null;
    }

    if (!Array.isArray(field)) {
      field = [field];
    }

    for (const { value, type } of field) {
      if (value && type) {
        const linkId = `node:${value}`;
        if (!seenNodesAndEdges.has(linkId)) {
          seenNodesAndEdges.add(linkId);
          const linkLabel = truncate(value, { length: MAX_VALUE_LENGTH });
          elements.nodes[linkId] = {
            data: {
              id: linkId,
              nodeType: 'link',
              label: linkLabel,
              color: palette.text.secondary,
              linkType: type,
              ...(type === 'instrument' && {
                instrumentId: value,
              }),
            },
          };
        }

        let numEntities = 0;
        for (const entityExternalId of entityExternalIds) {
          numEntities += 1;
          // Break when we hit limit of entities. Performance and stuff
          if (entityLimit && numEntities > entityLimit) {
            break;
          }
          const entityNodeId = `entity:${entityExternalId}`;
          if (!seenNodesAndEdges.has(entityNodeId)) {
            seenNodesAndEdges.add(entityExternalId);
            const entity = entities?.find(
              (ent) => ent.external_id === entityExternalId,
            );
            const entityLabel = entity
              ? `${entity.name_readable}\n(${entityExternalId})`
              : `(${entityExternalId})`;
            elements.nodes[entityNodeId] = {
              data: {
                id: entityNodeId,
                nodeType: 'entity',
                label: entityLabel,
                color: palette.colors.red.main,
                entityId: entityExternalId,
              },
            };
          }

          const edgeId = `${entityNodeId}___${linkId}`;
          if (!seenNodesAndEdges.has(edgeId)) {
            seenNodesAndEdges.add(edgeId);
            elements.edges[edgeId] = {
              data: {
                id: edgeId,
                target: entityNodeId,
                source: linkId,
              },
            };
          }
        }
      }
    }
  }
  return elements;
};

export const getRuleDelay = (
  executionDelayInDays: number | undefined = 0,
  executionDelayInHours: number | undefined = 0,
  executionDelayInMinutes: number | undefined = 0,
) => {
  const daysToMinutes = executionDelayInDays * 24 * 60;
  const hoursToMinutes = executionDelayInHours * 60;
  return daysToMinutes + hoursToMinutes + executionDelayInMinutes;
};
